import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { format } from 'date-fns';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { BuildingOffice2Icon, Cog6ToothIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { AuthContext } from "../AuthContext";

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const smtpProviderPresets = {
  custom: { label: 'Custom', host: '', port: '587', secure: false },
  gmail: { label: 'Gmail', host: 'smtp.gmail.com', port: '587', secure: false },
  outlook: { label: 'Outlook / Microsoft 365', host: 'smtp.office365.com', port: '587', secure: false },
  icloud: { label: 'iCloud', host: 'smtp.mail.me.com', port: '587', secure: false },
  yahoo: { label: 'Yahoo', host: 'smtp.mail.yahoo.com', port: '465', secure: true },
  ionos: { label: 'IONOS', host: 'smtp.ionos.co.uk', port: '465', secure: true },
};

export default function Leads() {
  const INITIAL_LEADS_RENDER_COUNT = 60;
  const LEADS_RENDER_BATCH_COUNT = 40;

  const parseJsonOrThrow = async (response, endpointLabel) => {
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    const raw = await response.text();

    if (!raw) {
      return null;
    }

    if (!contentType.includes('application/json')) {
      const preview = raw.slice(0, 140).replace(/\s+/g, ' ').trim();
      throw new Error(`${endpointLabel} returned non-JSON (${response.status}). ${preview}`);
    }

    try {
      return JSON.parse(raw);
    } catch (jsonError) {
      throw new Error(`${endpointLabel} returned invalid JSON (${response.status}).`);
    }
  };

  const parseJsonLenient = async (response, endpointLabel) => {
    try {
      return await parseJsonOrThrow(response, endpointLabel);
    } catch (_error) {
      return null;
    }
  };

  const [leads, setLeads] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [location, setLocation] = useState('');
  const [sector, setSector] = useState('');
  const [leadPullLoading, setLeadPullLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [sectorSummary, setSectorSummary] = useState(null);
  const [showPursuedOnly, setShowPursuedOnly] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [singleLeadEnrichingId, setSingleLeadEnrichingId] = useState(null);
  const [callScriptGeneratingId, setCallScriptGeneratingId] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportContent, setReportContent] = useState(null);
  const [reportOptionsVisible, setReportOptionsVisible] = useState(false);
  const [currentReportAvailable, setCurrentReportAvailable] = useState(false);
  const [newReportLoading, setNewReportLoading] = useState(false);
  const [availableReports, setAvailableReports] = useState([]);
  const [selectedReportIndex, setSelectedReportIndex] = useState(null);
  const [modalState, setModalState] = useState({ open: false, title: '', body: '' });
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [initialEmailEditingId, setInitialEmailEditingId] = useState(null);
  const [initialEmailDrafts, setInitialEmailDrafts] = useState({});
  const [initialEmailSubmittingId, setInitialEmailSubmittingId] = useState(null);
  const [initialEmailMessages, setInitialEmailMessages] = useState({});
  const [aiLocationPrompt, setAiLocationPrompt] = useState('');
  const [aiSectorPrompt, setAiSectorPrompt] = useState('');
  const [aiLocations, setAiLocations] = useState([]);
  const [aiSectors, setAiSectors] = useState([]);
  const [aiLocationsLoading, setAiLocationsLoading] = useState(false);
  const [aiSectorsLoading, setAiSectorsLoading] = useState(false);
  const [aiLeadPullRunning, setAiLeadPullRunning] = useState(false);
  const [aiLeadPullProgress, setAiLeadPullProgress] = useState('');
  const [bulkApplyFilteredOnly, setBulkApplyFilteredOnly] = useState(true);
  const [bulkInitialSendRunning, setBulkInitialSendRunning] = useState(false);
  const [bulkInitialSendFeedback, setBulkInitialSendFeedback] = useState('');
  const [bulkInitialSendError, setBulkInitialSendError] = useState('');
  const [bulkDeleteRunning, setBulkDeleteRunning] = useState(false);
  const [bulkDeleteFeedback, setBulkDeleteFeedback] = useState('');
  const [bulkDeleteError, setBulkDeleteError] = useState('');
  const [granularSendModal, setGranularSendModal] = useState({ open: false });
  const [granularSendQuantities, setGranularSendQuantities] = useState({});
  const [granularSendRunning, setGranularSendRunning] = useState(false);
  const [granularSendFeedback, setGranularSendFeedback] = useState('');
  const [granularSendError, setGranularSendError] = useState('');
  const [showLeadActions, setShowLeadActions] = useState(true);
  const [showBulkOperations, setShowBulkOperations] = useState(false);
  const [showFilteredOnly, setShowFilteredOnly] = useState(false);
  const [showCompaniesHouseHitOnly, setShowCompaniesHouseHitOnly] = useState(false);
  const [showContactedOnly, setShowContactedOnly] = useState(false);
  const [showLatestReplyPendingOnly, setShowLatestReplyPendingOnly] = useState(false);
  const [contactedFromDate, setContactedFromDate] = useState('');
  const [contactedToDate, setContactedToDate] = useState('');
  const [globalLeadSearch, setGlobalLeadSearch] = useState('');
  const [latestReplyDrafts, setLatestReplyDrafts] = useState({});
  const [latestReplySendingId, setLatestReplySendingId] = useState(null);
  const [latestReplyMessages, setLatestReplyMessages] = useState({});
  const [latestReplyEditorLeadId, setLatestReplyEditorLeadId] = useState(null);
  const [progressSavingLeadId, setProgressSavingLeadId] = useState(null);
  const [crmAddingLeadId, setCrmAddingLeadId] = useState(null);
  const [locationSortOrder, setLocationSortOrder] = useState('none'); // 'none', 'asc', 'desc'
  const [enrichCreditsToUse, setEnrichCreditsToUse] = useState(30);
  const [salesCredits, setSalesCredits] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  // Animation removed: credit updates will use company endpoint directly
  const [smtpModalOpen, setSmtpModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    Email: '',
    Telephone: '',
    CompanyName: '',
    Industry: '',
    Website: '',
    ContactName: '',
    Address: '',
    Postcode: '',
    Description: '',
    EmailFooter: '',
  });
  const [savedEmailFooterHtml, setSavedEmailFooterHtml] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsFeedback, setSettingsFeedback] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [passwordOne, setPasswordOne] = useState('');
  const [passwordTwo, setPasswordTwo] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [smtpForm, setSmtpForm] = useState({
    host: '',
    port: '587',
    username: '',
    password: '',
    fromEmail: '',
    secure: false,
  });
  const [smtpFeedback, setSmtpFeedback] = useState('');
  const [smtpError, setSmtpError] = useState('');
  const [smtpProvider, setSmtpProvider] = useState('custom');
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpChecking, setSmtpChecking] = useState(false);
  const [smtpConnected, setSmtpConnected] = useState(null);
  const [smtpConnectionMessage, setSmtpConnectionMessage] = useState('');
  const [smtpLastCheckedAt, setSmtpLastCheckedAt] = useState('');
  const [visibleLeadCount, setVisibleLeadCount] = useState(INITIAL_LEADS_RENDER_COUNT);
  const [filterIsSticky, setFilterIsSticky] = useState(false);
  const leadActionsRef = useRef(null);
  const filterSentinelRef = useRef(null);
  const reportsRef = useRef(null);
  const bulkOpsRef = useRef(null);
  const leadsTableRef = useRef(null);
  const leadsLoadMoreRef = useRef(null);

  const { user } = useContext(AuthContext);

  const permissionLevel = Number(user?.permission ?? user?.Permission ?? 0);
  const accessLevel = String(user?.access ?? user?.Access ?? '').trim().toLowerCase();
  const isFullAccessUser = accessLevel === 'admin' && permissionLevel === 1;
  const isPermissionRestricted = permissionLevel === 2 && !isFullAccessUser;
  const canUseAiAndEnrich = !isPermissionRestricted;
  const hasInsightAccount = user?.insightAccount !== null && user?.insightAccount !== undefined && String(user?.insightAccount).trim() !== '';

  console.log(user)

  const normalizeReportValue = (value) => {
    if (value === null || value === undefined) {
      return '';
    }

    let baseString = '';
    if (typeof value === 'string') {
      baseString = value;
    } else {
      try {
        baseString = JSON.stringify(value);
      } catch (jsonErr) {
        baseString = String(value);
      }
    }

    return baseString.replace(/\*\*/g, ' ');
  };

  const normalizeListFromPayload = (payload) => {
    if (!payload) return [];
    const candidate = payload?.data ?? payload?.items ?? payload;
    const toArray = Array.isArray(candidate)
      ? candidate
      : typeof candidate === 'string'
        ? candidate.split(/[;,\n]+/)
        : [];

    return toArray
      .map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '')))
      .filter(Boolean);
  };

  function buildPursuedSummary(rows) {
    return rows
      .filter((lead) => Number(lead.Pursue ?? 0) === 1)
      .reduce((acc, lead) => {
        const sectorKey = lead.Sector && lead.Sector.trim() ? lead.Sector : 'Unknown Sector';
        acc[sectorKey] = (acc[sectorKey] || 0) + 1;
        return acc;
      }, {});
  }

  // Centralised fetch so it can be reused after creating a pull
  const fetchLeads = async () => {
    setTableLoading(true);
    setError('');
    try {
      const response = await fetch('https://api.insightos.co.uk/sales/leadtable', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }

      const data = await parseJsonOrThrow(response, '/sales/leadtable');
      const leadRows = Array.isArray(data) ? data : data?.leads || [];
      setLeads(leadRows);
      setExpandedRows(new Set());
      if (showPursuedOnly) {
        setSectorSummary(buildPursuedSummary(leadRows));
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error loading leads');
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    const initialise = async () => {
      await Promise.all([fetchLeads(), fetchCompanyCredits(), fetchSmtpSettings()]);
    };

    initialise();
  }, []);

  const extractCompanyRow = (payload) => {
    const rowsCandidate = payload?.data;
    if (Array.isArray(rowsCandidate)) {
      if (rowsCandidate.length > 0 && Array.isArray(rowsCandidate[0])) {
        return rowsCandidate[0][0] ?? null;
      }
      return rowsCandidate[0] ?? null;
    }
    return rowsCandidate && typeof rowsCandidate === 'object' ? rowsCandidate : null;
  };

  const fetchCompanyCredits = async () => {
    setCreditsLoading(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/sales/company', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch company settings');
      }

      const payload = await parseJsonOrThrow(response, '/sales/company');
      const row = extractCompanyRow(payload);
      const nextSalesCredits = Number(row?.SalesCredits ?? 0);
      const nextCreditsUsed = Number(row?.CreditsUsed ?? 0);

      setSalesCredits(Number.isFinite(nextSalesCredits) ? nextSalesCredits : 0);
      setCreditsUsed(Number.isFinite(nextCreditsUsed) ? nextCreditsUsed : 0);
      setCompanyForm((prev) => ({
        ...prev,
        Email: String(row?.Email ?? ''),
        Telephone: String(row?.Telephone ?? ''),
        CompanyName: String(row?.CompanyName ?? ''),
        Industry: String(row?.Industry ?? ''),
        Website: String(row?.Website ?? ''),
        ContactName: String(row?.ContactName ?? ''),
        Address: String(row?.Address ?? ''),
        Postcode: String(row?.Postcode ?? ''),
        Description: String(row?.Description ?? ''),
        EmailFooter: String(row?.EmailFooter ?? ''),
      }));
      setSavedEmailFooterHtml(String(row?.EmailFooter ?? ''));
    } catch (err) {
      console.error(err);
    } finally {
      setCreditsLoading(false);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/sales/smptp', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const payload = await parseJsonOrThrow(response, '/sales/smptp');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to load SMTP settings');
      }

      const data = payload?.data || {};
      const nextHost = String(data?.host || '').trim();
      const nextPort = String(data?.port || '587').trim() || '587';
      const nextUsername = String(data?.username || '').trim();
      const nextPassword = String(data?.password || '').trim();
      const nextSecure = data?.secure === true;

      setSmtpForm((prev) => ({
        ...prev,
        host: nextHost,
        port: nextPort,
        username: nextUsername,
        password: nextPassword,
        secure: nextSecure,
        fromEmail: prev.fromEmail || nextUsername,
      }));

      const inferredProvider = Object.entries(smtpProviderPresets).find(
        ([key, preset]) => key !== 'custom' && preset.host.toLowerCase() === nextHost.toLowerCase()
      );
      setSmtpProvider(inferredProvider ? inferredProvider[0] : 'custom');
    } catch (err) {
      console.error(err);
      setSmtpError('Unable to load SMTP settings from server.');
    }
  };

  const checkSmtpConnection = async (override = null) => {
    const current = override || smtpForm;
    const hasMinimum = String(current?.host || '').trim() && String(current?.username || '').trim() && String(current?.password || '').trim();

    if (!hasMinimum) {
      setSmtpConnected(null);
      setSmtpConnectionMessage('Enter host, username, and password to check connection.');
      return;
    }

    setSmtpChecking(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/sales/smptp/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(current),
      });

      const payload = await parseJsonOrThrow(response, '/sales/smptp/check');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Connection check failed');
      }

      const connected = payload?.connected === true;
      setSmtpConnected(connected);
      setSmtpConnectionMessage(String(payload?.message || (connected ? 'Connected' : 'Not connected')));
      setSmtpLastCheckedAt(new Date().toLocaleTimeString());
    } catch (err) {
      console.error(err);
      setSmtpConnected(false);
      setSmtpConnectionMessage(err.message || 'Connection check failed');
      setSmtpLastCheckedAt(new Date().toLocaleTimeString());
    } finally {
      setSmtpChecking(false);
    }
  };

  useEffect(() => {
    const hasMinimum = smtpForm.host.trim() && smtpForm.username.trim() && smtpForm.password.trim();
    if (!hasMinimum) {
      setSmtpConnected(null);
      setSmtpConnectionMessage('Enter host, username, and password to check connection.');
      return;
    }

    const timer = window.setTimeout(() => {
      checkSmtpConnection();
    }, 900);

    return () => window.clearTimeout(timer);
  }, [smtpForm.host, smtpForm.port, smtpForm.username, smtpForm.password, smtpForm.secure]);

  const handleLeadPull = async () => {
    const payload = {
      location,
      sector,
    };

    setLeadPullLoading(true);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/sales/places-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await parseJsonOrThrow(response, '/sales/places-search');
      console.log(data);

      if (!response.ok) {
        throw new Error('Failed to pull new lead');
      }

      await fetchLeads();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error pulling new lead');
    } finally {
      setLeadPullLoading(false);
    }
  };

  const requestAiList = async (endpoint, promptValue, setter, loadingSetter, label) => {
    if (!promptValue.trim()) {
      setError(`Please provide a prompt for ${label}.`);
      return;
    }

    loadingSetter(true);
    setError('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt: promptValue.trim() }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${label}`);
      }

      const payload = await parseJsonOrThrow(response, endpoint);
      setter(normalizeListFromPayload(payload));
    } catch (err) {
      console.error(err);
      setError(err.message || `Unable to load ${label}.`);
      setter([]);
    } finally {
      loadingSetter(false);
    }
  };

  const handleAiLocationsRequest = () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow AI features.');
      return;
    }
    requestAiList('https://api.insightos.co.uk/sales/ai/locations', aiLocationPrompt, setAiLocations, setAiLocationsLoading, 'towns/areas');
  };

  const handleAiSectorsRequest = () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow AI features.');
      return;
    }
    requestAiList('https://api.insightos.co.uk/sales/ai/sectors', aiSectorPrompt, setAiSectors, setAiSectorsLoading, 'sectors');
  };

  const removeAiLocation = (value) => {
    setAiLocations((prev) => prev.filter((item) => item !== value));
  };

  const removeAiSector = (value) => {
    setAiSectors((prev) => prev.filter((item) => item !== value));
  };

  const callLeadPullOnce = async (loc, sec) => {
    const payload = { location: loc, sector: sec };
    const response = await fetch('https://api.insightos.co.uk/sales/places-search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to pull lead for ${loc} - ${sec}`);
    }
  };

  const handleAiLeadPullLoop = async (creditsLimit = null) => {
    const locationsToUse = (aiLocations.length ? aiLocations : [location]).map((item) => item.trim()).filter(Boolean);
    const sectorsToUse = (aiSectors.length ? aiSectors : [sector]).map((item) => item.trim()).filter(Boolean);

    if (locationsToUse.length === 0 || sectorsToUse.length === 0) {
      setError('Please provide at least one town/area and one sector (manually or via AI).');
      return;
    }

    setAiLeadPullRunning(true);
    setAiLeadPullProgress('');
    setError('');

    try {
      let completed = 0;
      const total = locationsToUse.length * sectorsToUse.length;
      const cappedTotal = total; // run all combinations (unlimited)

      for (const loc of locationsToUse) {
        for (const sec of sectorsToUse) {
          setAiLeadPullProgress(`Pulling leads for ${loc} - ${sec} (${completed + 1}/${cappedTotal})...`);
          await callLeadPullOnce(loc, sec);
          completed += 1;
        }
      }

      setAiLeadPullProgress('Refreshing leads...');
      await fetchLeads();
      setAiLeadPullProgress('Completed AI lead pull loop.');
    } catch (err) {
      console.error(err);
      setError('Lead pull loop failed on one of the combinations.');
    } finally {
      setAiLeadPullRunning(false);
      setTimeout(() => setAiLeadPullProgress(''), 4000);
    }
  };

  const handleGenerate = async (limitOverride = null) => {
    setFilterLoading(true);
    setError('');

    try {
      const parsedLimit = Number(limitOverride);
      const fallbackLimit = 500;
      let remainingBudget = Number.isFinite(parsedLimit) && parsedLimit > 0 ? Math.floor(parsedLimit) : fallbackLimit;
      let enrichedCount = 0;

      if (remainingBudget <= 0) {
        throw new Error('Please provide a valid enrich batch limit.');
      }

      while (remainingBudget >= 2) {
        const locationParam = location.trim() ? `&location=${encodeURIComponent(location.trim())}` : '';
        const sectorParam = sector.trim() ? `&sector=${encodeURIComponent(sector.trim())}` : '';
        const endpoint = `https://api.insightos.co.uk/sales/filter?limit=${encodeURIComponent(remainingBudget)}&sync=1${locationParam}${sectorParam}`;

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        const payload = await parseJsonLenient(response, endpoint);

        if (!response.ok) {
          throw new Error(payload?.message || 'Failed to filter leads');
        }

        const reports = Array.isArray(payload?.reports) ? payload.reports : [];
        const enrichedLeadsPayload = Array.isArray(payload?.enrichedLeads) ? payload.enrichedLeads : [];
        const successfulReportItems = reports.filter((item) => item?.success);
        const enrichedLeads = enrichedLeadsPayload.length ? enrichedLeadsPayload : successfulReportItems;
        const creditsPayload = payload?.credits || null;

        console.log('[Leads][Enrich][Sync] /leads/filter response', {
          requestedLimit: remainingBudget,
          processed: payload?.processed,
          reportsCount: reports.length,
          successfulReportsCount: successfulReportItems.length,
          enrichedLeadsPayloadCount: enrichedLeadsPayload.length,
          enrichedLeadsCount: enrichedLeads.length,
          credits: creditsPayload,
          maxLeadsAllowed: creditsPayload?.maxLeadsAllowed,
          leadsEnriched: creditsPayload?.leadsEnriched,
          syncMode: creditsPayload?.syncMode,
        });

        if (!enrichedLeads.length) {
          console.warn('[Leads][Enrich][Sync] No enriched lead returned; stopping loop.', {
            requestedLimit: remainingBudget,
            processed: payload?.processed,
            reportsCount: reports.length,
            credits: creditsPayload,
          });
          break;
        }

        const reportItem = enrichedLeads[0];
        const companiesHouseHitFlag =
          Number(
            reportItem?.companiesHouseHit ??
              reportItem?.companieshousehit ??
              reportItem?.report?.companiesHouseHit ??
              reportItem?.report?.CompaniesHouseHit ??
              0
          ) === 1;
        const creditsForLeadRaw = Number(reportItem?.creditsUsed ?? reportItem?.creditUsed ?? reportItem?.credits);
        const creditsForLead = Number.isFinite(creditsForLeadRaw) && creditsForLeadRaw > 0
          ? creditsForLeadRaw
          : companiesHouseHitFlag
          ? 3
          : 2;

        const remainingFromStepRaw = Number(
          reportItem?.salesCreditsRemaining ?? reportItem?.remainingSalesCredits ?? reportItem?.remainingSalesCredit
        );
        const remainingFromCreditsPayloadRaw = Number(creditsPayload?.salesCredits ?? creditsPayload?.salesCredit);
        const nextRemainingSalesCredit = Number.isFinite(remainingFromStepRaw)
          ? Math.max(0, remainingFromStepRaw)
          : Number.isFinite(remainingFromCreditsPayloadRaw)
          ? Math.max(0, remainingFromCreditsPayloadRaw)
          : Math.max(0, (Number(salesCredits) || 0) - creditsForLead);

        const perLeadCreditsUsedTotalRaw = Number(reportItem?.creditsUsedTotal);
        const creditsUsedFromPayloadRaw = Number(creditsPayload?.creditsUsed);
        const nextCreditsUsedTotal = Number.isFinite(perLeadCreditsUsedTotalRaw)
          ? Math.max(0, perLeadCreditsUsedTotalRaw)
          : Number.isFinite(creditsUsedFromPayloadRaw)
          ? Math.max(0, creditsUsedFromPayloadRaw)
          : Math.max(0, (Number(creditsUsed) || 0) + creditsForLead);

        const animationStep = {
          id: reportItem?.id,
          creditsUsed: creditsForLead,
          creditsUsedTotal: nextCreditsUsedTotal,
          companiesHouseHit: companiesHouseHitFlag,
          remainingSalesCredit: nextRemainingSalesCredit,
          company:
            String(
              reportItem?.company ||
                reportItem?.Company ||
                reportItem?.report?.Company ||
                reportItem?.report?.companyName ||
                reportItem?.report?.company ||
                ''
            ).trim() || 'Lead',
        };

        // Refresh company settings via company endpoint instead of running animation
        try {
          await fetchCompanyCredits();
        } catch (err) {
          console.error('Failed to refresh company settings', err);
          // Fallback to local values if payload provided
          setSalesCredits(nextRemainingSalesCredit);
          setCreditsUsed(nextCreditsUsedTotal);
        }

        remainingBudget = Math.max(0, remainingBudget - 1);

        enrichedCount += 1;
      }

      if (!enrichedCount) {
        console.warn('[Leads][Enrich][Sync] Loop completed without enriched leads.');
      }

      await fetchLeads();
      setShowPursuedOnly(false);
      // Refresh company settings after bulk enrich
      try {
        await fetchCompanyCredits();
      } catch (err) {
        console.error('Failed to refresh company settings after enrich', err);
      }
      setSectorSummary(null);
      setReportContent(null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error filtering leads');
    } finally {
      setFilterLoading(false);
    }
  };

  // Animation removed: no effect required for usage animation

  // Pursued summary modal/section removed; pursue filter now toggles directly via button

  const handleDeleteLead = async (rowId) => {
    if (!rowId) {
      setError('Cannot delete lead without an identifier.');
      return;
    }

    setDeletingLeadId(rowId);
    setError('');

    try {
      const response = await fetch(`https://api.insightos.co.uk/sales/leads/${rowId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete lead');
      }

      await fetchLeads();
    } catch (err) {
      console.error(err);
      setError('Error deleting lead');
    } finally {
      setDeletingLeadId(null);
    }
  };

  const handleSingleLeadEnrich = async (lead) => {
    const leadId = Number(lead?.id);
    const filterValue = Number(lead?.Filter ?? 0);

    if (!Number.isInteger(leadId) || leadId <= 0) {
      setError('Cannot enrich lead without a valid identifier.');
      return;
    }

    if (filterValue === 1) {
      setError('This lead is already enriched (Filter = 1).');
      return;
    }

    setSingleLeadEnrichingId(leadId);
    setError('');

    try {
      const response = await fetch(`https://api.insightos.co.uk/sales/indfilter/${leadId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to enrich selected lead');
      }

      await fetchLeads();
      await fetchCompanyCredits();
    } catch (err) {
      console.error(err);
      setError('Error enriching selected lead.');
    } finally {
      setSingleLeadEnrichingId(null);
    }
  };

  const handleGenerateCallScript = async (lead) => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow AI features.');
      return;
    }

    const leadId = Number(lead?.id);
    if (!Number.isInteger(leadId) || leadId <= 0) {
      setError('Cannot generate call script without a valid lead id.');
      return;
    }

    setCallScriptGeneratingId(leadId);
    setError('');

    try {
      const response = await fetch(`https://api.insightos.co.uk/sales/${leadId}/call-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const payload = await parseJsonOrThrow(response, `/leads/${leadId}/call-script`);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to generate call script');
      }

      const script = String(payload?.script || '').trim();
      if (!script) {
        throw new Error('No call script was returned.');
      }

      setLeads((prevLeads) =>
        prevLeads.map((existingLead) =>
          existingLead.id === leadId
            ? {
                ...existingLead,
                CallScript: script,
              }
            : existingLead
        )
      );

      openModal(`Call Script - ${lead?.Company || lead?.LeadName || 'Lead'}`, script);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to generate call script.');
    } finally {
      setCallScriptGeneratingId(null);
    }
  };

  const parseLeadContactDate = (lead) => {
    const value = lead?.LastContacted || lead?.Contacted;
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isLeadWithinContactDateRange = (lead) => {
    const hasFrom = Boolean(contactedFromDate);
    const hasTo = Boolean(contactedToDate);

    if (!hasFrom && !hasTo) {
      return true;
    }

    const contactDate = parseLeadContactDate(lead);
    if (!contactDate) {
      return false;
    }

    if (hasFrom) {
      const start = new Date(`${contactedFromDate}T00:00:00`);
      if (contactDate < start) {
        return false;
      }
    }

    if (hasTo) {
      const end = new Date(`${contactedToDate}T23:59:59.999`);
      if (contactDate > end) {
        return false;
      }
    }

    return true;
  };

  const handleProgressChange = async (leadId, progress) => {
    if (!leadId) {
      return;
    }

    const allowedProgress = new Set(['No Reply', 'Interested', 'Sale']);
    if (!allowedProgress.has(progress)) {
      setError('Invalid progress value selected.');
      return;
    }

    setProgressSavingLeadId(leadId);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/sales/progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ leadId, progress }),
      });

      const payload = await parseJsonLenient(response, '/sales/progress');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to update lead progress.');
      }

      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId
            ? {
                ...lead,
                Progress: progress,
              }
            : lead
        )
      );
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Unable to update progress.');
    } finally {
      setProgressSavingLeadId(null);
    }
  };

  const handleAddToCrm = async (lead) => {
    const leadId = Number(lead?.id);
    if (!Number.isInteger(leadId) || leadId <= 0) {
      setError('Cannot add to CRM without a valid lead id.');
      return;
    }

    setCrmAddingLeadId(leadId);
    window.location.assign('https://app.insightos.co.uk/avenor');
  };

  const isLatestReplyPending = (lead) => {
    const value = String(lead?.LatestReply ?? '').trim();
    return value.length > 0;
  };

  const pendingLatestReplyCount = useMemo(
    () => leads.filter((lead) => isLatestReplyPending(lead)).length,
    [leads]
  );

  const handleLatestReplyDraftChange = (leadId, value) => {
    if (!leadId) return;
    setLatestReplyDrafts((prev) => ({
      ...prev,
      [leadId]: value,
    }));
  };

  const openLatestReplyEditor = (lead) => {
    if (!lead?.id) return;

    setLatestReplyDrafts((prev) => ({
      ...prev,
      [lead.id]: String(prev[lead.id] ?? lead?.LatestReply ?? ''),
    }));
    setLatestReplyEditorLeadId(lead.id);
  };

  const closeLatestReplyEditor = () => {
    setLatestReplyEditorLeadId(null);
  };

  const handleApproveLatestReply = async (lead) => {
    const leadId = Number(lead?.id);
    if (!Number.isInteger(leadId) || leadId <= 0) {
      setError('Cannot send latest reply without a valid lead id.');
      return;
    }

    const latestReplyEmail = String(lead?.LatestReplyEmail ?? lead?.Email ?? '').trim();
    const replyBody = String(latestReplyDrafts[leadId] ?? lead?.LatestReply ?? '').trim();

    if (!latestReplyEmail) {
      setLatestReplyMessages((prev) => ({
        ...prev,
        [leadId]: { error: 'Missing LatestReplyEmail for this lead.' },
      }));
      return;
    }

    if (!replyBody) {
      setLatestReplyMessages((prev) => ({
        ...prev,
        [leadId]: { error: 'Latest reply text is empty.' },
      }));
      return;
    }

    setLatestReplySendingId(leadId);
    setLatestReplyMessages((prev) => ({ ...prev, [leadId]: null }));

    try {
      const response = await fetch('https://api.insightos.co.uk/admin/approvereply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          leadId,
          email: latestReplyEmail,
          latestReplyEmail,
          reply: replyBody,
          latestReply: replyBody,
        }),
      });

      const payload = await parseJsonLenient(response, '/admin/approvereply');
      if (!response.ok || (payload && payload.success === false)) {
        throw new Error(payload?.message || 'Failed to approve and send latest reply.');
      }

      setLeads((prevLeads) =>
        prevLeads.map((existingLead) =>
          existingLead.id === leadId
            ? {
                ...existingLead,
                LatestReply: replyBody,
                LatestReplySent: 1,
              }
            : existingLead
        )
      );

      setLatestReplyMessages((prev) => ({
        ...prev,
        [leadId]: { success: 'Latest reply approved and sent.' },
      }));
    } catch (err) {
      console.error(err);
      setLatestReplyMessages((prev) => ({
        ...prev,
        [leadId]: { error: err?.message || 'Unable to send latest reply.' },
      }));
    } finally {
      setLatestReplySendingId(null);
    }
  };

  const filteredLeads = useMemo(() => {
    const locationQuery = location.trim().toLowerCase();
    const sectorQuery = sector.trim().toLowerCase();
    const globalSearchQuery = globalLeadSearch.trim().toLowerCase();

    const filtered = leads.filter((lead) => {
      const matchesLocation = !locationQuery || String(lead.Location ?? '').toLowerCase().includes(locationQuery);
      const matchesSector = !sectorQuery || String(lead.Sector ?? '').toLowerCase().includes(sectorQuery);

      const filterValue = Number(lead.Filter ?? 0);
      const pursueValue = Number(lead.Pursue ?? 0);
      const companiesHouseHitValue = Number(lead.CompaniesHouseHit ?? 0);
      const matchesPursue = !showPursuedOnly || pursueValue === 1;
      const matchesCompaniesHouseHit = !showCompaniesHouseHitOnly || companiesHouseHitValue === 1;
      const hasContactDate = Boolean(parseLeadContactDate(lead));
      const matchesContacted = !showContactedOnly || hasContactDate;
      const matchesLatestReplyPending = !showLatestReplyPendingOnly || isLatestReplyPending(lead);
      const matchesContactDateRange = isLeadWithinContactDateRange(lead);

      // When `showFilteredOnly` is enabled, include leads that are either Filter===1 OR CompaniesHouseHit===1
      const matchesFiltered = !showFilteredOnly || filterValue === 1 || companiesHouseHitValue === 1;
      const matchesGlobalSearch =
        !globalSearchQuery ||
        Object.values(lead || {}).some((value) => String(value ?? '').toLowerCase().includes(globalSearchQuery));

      return (
        matchesLocation &&
        matchesSector &&
        matchesPursue &&
        matchesCompaniesHouseHit &&
        matchesFiltered &&
        matchesContacted &&
        matchesLatestReplyPending &&
        matchesContactDateRange &&
        matchesGlobalSearch
      );
    });

    // Apply alphabetical sorting by Location
    if (locationSortOrder === 'asc') {
      return [...filtered].sort((a, b) => {
        const locA = String(a.Location ?? '').toLowerCase();
        const locB = String(b.Location ?? '').toLowerCase();
        return locA.localeCompare(locB);
      });
    } else if (locationSortOrder === 'desc') {
      return [...filtered].sort((a, b) => {
        const locA = String(a.Location ?? '').toLowerCase();
        const locB = String(b.Location ?? '').toLowerCase();
        return locB.localeCompare(locA);
      });
    }

    return filtered;
  }, [
    leads,
    location,
    sector,
    showPursuedOnly,
    showCompaniesHouseHitOnly,
    showFilteredOnly,
    showContactedOnly,
    showLatestReplyPendingOnly,
    contactedFromDate,
    contactedToDate,
    globalLeadSearch,
    locationSortOrder,
  ]);

  const filteredByGenerateCount = useMemo(() => {
    return leads.filter((lead) => Number(lead.Filter ?? 0) === 1).length;
  }, [leads]);

  const visibleFilteredLeads = useMemo(() => {
    return filteredLeads.slice(0, visibleLeadCount);
  }, [filteredLeads, visibleLeadCount]);

  const hasMoreFilteredLeads = visibleLeadCount < filteredLeads.length;

  useEffect(() => {
    setVisibleLeadCount(INITIAL_LEADS_RENDER_COUNT);
  }, [
    filteredLeads.length,
    location,
    sector,
    showFilteredOnly,
    showCompaniesHouseHitOnly,
    showPursuedOnly,
    showContactedOnly,
    showLatestReplyPendingOnly,
    contactedFromDate,
    contactedToDate,
    globalLeadSearch,
    locationSortOrder,
  ]);

  const contactedInDateRangeCount = useMemo(() => {
    return leads.filter((lead) => parseLeadContactDate(lead) && isLeadWithinContactDateRange(lead)).length;
  }, [leads, contactedFromDate, contactedToDate]);

  useEffect(() => {
    if (!hasMoreFilteredLeads || !leadsLoadMoreRef.current) {
      return;
    }

    const target = leadsLoadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) {
          return;
        }

        setVisibleLeadCount((prev) => Math.min(prev + LEADS_RENDER_BATCH_COUNT, filteredLeads.length));
      },
      {
        root: null,
        rootMargin: '240px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreFilteredLeads, filteredLeads.length]);

  useEffect(() => {
    const sentinel = filterSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setFilterIsSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { root: null, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const enrichPreview = useMemo(() => {
    const target = showFilteredOnly
      ? filteredLeads.filter((lead) => Number(lead.Filter ?? 0) === 1)
      : filteredLeads;
    const targetCount = target.length;
    const companiesHouseHitCount = target.filter((lead) => Number(lead.CompaniesHouseHit ?? 0) === 1).length;
    const minimumPerLeadCredits = 2;
    const perLeadCredits = 3;
    const selectedCredits = Math.max(0, Number(enrichCreditsToUse) || 0);
    const maxLeadsByCredits = Math.floor(selectedCredits / perLeadCredits);
    const leadsToEnrich = Math.min(targetCount, maxLeadsByCredits);
    return {
      perLeadCredits,
      targetCount,
      companiesHouseHitCount,
      selectedCredits,
      maxLeadsByCredits,
      leadsToEnrich,
      minimumPerLeadCredits,
      minimumCreditsForAll: targetCount * minimumPerLeadCredits,
      estimatedCreditsForAll: targetCount * perLeadCredits,
      creditsToConsumeNow: leadsToEnrich * perLeadCredits,
    };
  }, [filteredLeads, enrichCreditsToUse, showFilteredOnly]);

  const dashboardStats = useMemo(() => {
    const totalLeads = leads.length;
    const pursuedLeads = leads.filter((lead) => Number(lead.Pursue ?? 0) === 1).length;
    const filteredCount = leads.filter((lead) => Number(lead.Filter ?? 0) === 1).length;
    const leadsWithInitialEmails = leads.filter((lead) => (lead.InitialEmail ?? '').trim()).length;
    const leadsContacted = leads.filter((lead) => lead.Contacted).length;
    const leadsResponded = leads.filter((lead) => lead.Responded).length;
    const companiesHouseHitCount = leads.filter((lead) => Number(lead.CompaniesHouseHit ?? 0) === 1).length;

    // Top 5 locations
    const locationCounts = leads.reduce((acc, lead) => {
      const loc = lead.Location?.trim() || 'Unknown';
      acc[loc] = (acc[loc] || 0) + 1;
      return acc;
    }, {});
    const topLocations = Object.entries(locationCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    // Top 5 sectors
    const sectorCounts = leads.reduce((acc, lead) => {
      const sec = lead.Sector?.trim() || 'Unknown';
      acc[sec] = (acc[sec] || 0) + 1;
      return acc;
    }, {});
    const topSectors = Object.entries(sectorCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return {
      totalLeads,
      pursuedLeads,
      filteredCount,
      leadsWithInitialEmails,
      leadsContacted,
      leadsResponded,
      companiesHouseHitCount,
      topLocations,
      topSectors,
      pursuePercentage: totalLeads > 0 ? ((pursuedLeads / totalLeads) * 100).toFixed(1) : 0,
      companiesHousePercentage: totalLeads > 0 ? ((companiesHouseHitCount / totalLeads) * 100).toFixed(1) : 0,
      
    };
  }, [leads]);

  const availableLocations = useMemo(() => {
    const set = new Set();
    leads.forEach((lead) => {
      if (lead.Location && lead.Location.trim()) {
        set.add(lead.Location);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  const locationOptionsForActiveFilters = useMemo(() => {
    const options = new Set();

    leads
      .filter((lead) => {
        const filterValue = Number(lead.Filter ?? 0);
        const companiesHouseHitValue = Number(lead.CompaniesHouseHit ?? 0);
        const matchesFiltered = !showFilteredOnly || filterValue === 1 || companiesHouseHitValue === 1;
        const matchesCompaniesHouseHit = !showCompaniesHouseHitOnly || companiesHouseHitValue === 1;
        return matchesFiltered && matchesCompaniesHouseHit;
      })
      .forEach((lead) => {
        const nextLocation = String(lead.Location ?? '').trim();
        if (nextLocation) {
          options.add(nextLocation);
        }
      });

    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [leads, showFilteredOnly, showCompaniesHouseHitOnly]);

  const sectorOptionsForActiveFilters = useMemo(() => {
    const options = new Set();

    leads
      .filter((lead) => {
        if (showFilteredOnly && Number(lead.Filter ?? 0) !== 1) return false;
        if (showCompaniesHouseHitOnly && Number(lead.CompaniesHouseHit ?? 0) !== 1) return false;
        return true;
      })
      .forEach((lead) => {
        const value = String(lead?.Sector ?? '').trim();
        if (value) options.add(value);
      });

    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [leads, showFilteredOnly, showCompaniesHouseHitOnly]);

  const sectorLocationBreakdown = useMemo(() => {
    const targetLeads = (bulkApplyFilteredOnly ? filteredLeads : leads).filter(
      (lead) => lead?.id && (lead.InitialEmail ?? '').trim()
    );
    const breakdown = {};
    for (const lead of targetLeads) {
      const sector = lead.Sector ?? 'Unknown';
      const location = lead.Location ?? 'Unknown';
      const key = `${sector}|||${location}`;
      if (!breakdown[key]) {
        breakdown[key] = { sector, location, count: 0, leads: [] };
      }
      breakdown[key].count += 1;
      breakdown[key].leads.push(lead);
    }
    return breakdown;
  }, [leads, filteredLeads, bulkApplyFilteredOnly]);

  const locationSelectValue = availableLocations.includes(location) ? location : '';
  const sectorSelectValue = sectorOptionsForActiveFilters.includes(sector) ? sector : '';

  const editingLead = useMemo(
    () => leads.find((lead) => lead.id === initialEmailEditingId),
    [leads, initialEmailEditingId]
  );
  const latestReplyEditingLead = useMemo(
    () => leads.find((lead) => lead.id === latestReplyEditorLeadId),
    [leads, latestReplyEditorLeadId]
  );

  const editingDraft = editingLead ? initialEmailDrafts[editingLead.id] ?? '' : '';
  const editingMessage = editingLead ? initialEmailMessages[editingLead.id] : null;
  const editingSubmitting = editingLead ? initialEmailSubmittingId === editingLead.id : false;

  const openModal = (title, text) => {
    setModalState({ open: true, title, body: text });
  };

  const closeModal = () => {
    setModalState({ open: false, title: '', body: '' });
  };

  const actionsDisabled = tableLoading || leadPullLoading || filterLoading || aiLeadPullRunning;

  const toggleRowExpansion = (rowId) => {
    if (rowId === undefined || rowId === null) {
      return;
    }

    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleBulkInitialEmailSend = async () => {
    const targetLeads = (bulkApplyFilteredOnly ? filteredLeads : leads).filter((lead) => lead?.id && (lead.InitialEmail ?? '').trim());

    if (targetLeads.length === 0) {
      setBulkInitialSendError('No drafted initial emails found to send.');
      return;
    }

    setBulkInitialSendRunning(true);
    setBulkInitialSendFeedback('');
    setBulkInitialSendError('');

    let sent = 0;
    let failed = 0;

    try {
      for (const lead of targetLeads) {
        try {
          const response = await fetch(`https://api.insightos.co.uk/sales/leads/${lead.id}/initial-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ initialEmail: lead.InitialEmail }),
          });

          if (!response.ok) {
            throw new Error('Bulk initial email failed');
          }
          sent += 1;
        } catch (innerErr) {
          console.error(innerErr);
          failed += 1;
        }
      }

      setBulkInitialSendFeedback(`Sent initial emails. Success: ${sent}, Failed: ${failed}.`);
    } catch (err) {
      console.error(err);
      setBulkInitialSendError('Bulk initial email send failed.');
    } finally {
      setBulkInitialSendRunning(false);
    }
  };

  const openGranularSendModal = () => {
    const initialQuantities = {};
    Object.keys(sectorLocationBreakdown).forEach((key) => {
      initialQuantities[key] = sectorLocationBreakdown[key].count;
    });
    setGranularSendQuantities(initialQuantities);
    setGranularSendFeedback('');
    setGranularSendError('');
    setGranularSendModal({ open: true });
  };

  const closeGranularSendModal = () => {
    setGranularSendModal({ open: false });
    setGranularSendQuantities({});
    setGranularSendFeedback('');
    setGranularSendError('');
  };

  const handleGranularSendQuantityChange = (key, value) => {
    const parsed = parseInt(value, 10);
    const max = sectorLocationBreakdown[key]?.count ?? 0;
    const clamped = Math.max(0, Math.min(isNaN(parsed) ? 0 : parsed, max));
    setGranularSendQuantities((prev) => ({ ...prev, [key]: clamped }));
  };

  const handleGranularSendEmails = async () => {
    const toSend = [];
    Object.keys(granularSendQuantities).forEach((key) => {
      const qty = granularSendQuantities[key] ?? 0;
      if (qty > 0 && sectorLocationBreakdown[key]) {
        const available = sectorLocationBreakdown[key].leads;
        toSend.push(...available.slice(0, qty));
      }
    });

    if (toSend.length === 0) {
      setGranularSendError('No emails selected to send.');
      return;
    }

    setGranularSendRunning(true);
    setGranularSendFeedback('');
    setGranularSendError('');

    let sent = 0;
    let failed = 0;

    try {
      for (const lead of toSend) {
        try {
          const response = await fetch(`https://api.insightos.co.uk/sales/leads/${lead.id}/initial-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ initialEmail: lead.InitialEmail }),
          });

          if (!response.ok) {
            throw new Error('Granular email send failed');
          }
          sent += 1;
        } catch (innerErr) {
          console.error(innerErr);
          failed += 1;
        }
      }

      setGranularSendFeedback(`Sent ${sent} email(s). Failed: ${failed}.`);
    } catch (err) {
      console.error(err);
      setGranularSendError('Granular email send failed.');
    } finally {
      setGranularSendRunning(false);
    }
  };

  const handleBulkDeleteLeads = async () => {
    const targetLeads = (bulkApplyFilteredOnly ? filteredLeads : leads).filter((lead) => lead?.id);

    if (targetLeads.length === 0) {
      setBulkDeleteError('No leads found to delete.');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${targetLeads.length} lead(s)? This cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    setBulkDeleteRunning(true);
    setBulkDeleteFeedback('');
    setBulkDeleteError('');

    let deleted = 0;
    let failed = 0;

    try {
      for (const lead of targetLeads) {
        try {
          const response = await fetch(`https://api.insightos.co.uk/sales/leads/${lead.id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error('Delete failed');
          }
          deleted += 1;
        } catch (innerErr) {
          console.error(innerErr);
          failed += 1;
        }
      }

      setBulkDeleteFeedback(`Deleted leads. Success: ${deleted}, Failed: ${failed}.`);
      await fetchLeads();
    } catch (err) {
      console.error(err);
      setBulkDeleteError('Bulk delete operation failed.');
    } finally {
      setBulkDeleteRunning(false);
    }
  };

  const openInitialEmailEditor = (lead) => {
    if (!lead?.id) {
      setError('Cannot edit the initial email without a lead identifier.');
      return;
    }

    setInitialEmailEditingId(lead.id);
    setInitialEmailDrafts({ [lead.id]: lead.InitialEmail ?? '' });
    setInitialEmailMessages((prev) => ({
      ...prev,
      [lead.id]: { success: '', error: '' },
    }));
  };

  const closeInitialEmailEditor = (leadId, options = {}) => {
    const targetId = leadId ?? initialEmailEditingId;
    if (!targetId) {
      setInitialEmailEditingId(null);
      return;
    }

    setInitialEmailEditingId((current) => (current === targetId ? null : current));
    setInitialEmailDrafts((prev) => {
      if (!(targetId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[targetId];
      return next;
    });

    if (options.resetMessage) {
      setInitialEmailMessages((prev) => {
        if (!(targetId in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
    }
  };

  const handleInitialEmailDraftChange = (leadId, value) => {
    if (!leadId) {
      return;
    }

    setInitialEmailDrafts((prev) => ({
      ...prev,
      [leadId]: value,
    }));
  };

  const submitInitialEmail = async (lead) => {
    const leadId = lead?.id;

    if (!leadId) {
      setError('Cannot send the initial email without a lead identifier.');
      return;
    }

    const draft = initialEmailDrafts[leadId] ?? '';
    if (!draft.trim()) {
      setInitialEmailMessages((prev) => ({
        ...prev,
        [leadId]: { success: '', error: 'Please provide email content before sending.' },
      }));
      return;
    }

    setInitialEmailSubmittingId(leadId);
    setInitialEmailMessages((prev) => ({
      ...prev,
      [leadId]: { success: '', error: '' },
    }));

    try {
      const response = await fetch(`https://api.insightos.co.uk/sales/leads/${leadId}/initial-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ initialEmail: draft }),
      });

      const payload = await parseJsonLenient(response, '/sales/leads/:id/initial-email');

      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to send initial email');
      }

      const successfulOfficerEmails = Array.isArray(payload?.data?.successfulOfficerEmails)
        ? payload.data.successfulOfficerEmails
        : [];
      const failedOfficerEmails = Array.isArray(payload?.data?.failedOfficerEmails)
        ? payload.data.failedOfficerEmails
        : [];
      const sentTo = String(payload?.data?.sentTo || '').trim();
      const progressValue = String(payload?.data?.progress || 'Contacted').trim();
      const lastContactedValue = payload?.data?.lastContacted || new Date().toISOString();

      const resultSummary = [
        sentTo ? `Sent to: ${sentTo}` : null,
        successfulOfficerEmails.length ? `Officer success: ${successfulOfficerEmails.length}` : null,
        failedOfficerEmails.length ? `Officer failed: ${failedOfficerEmails.length}` : null,
      ].filter(Boolean).join(' • ');

      setInitialEmailMessages((prev) => ({
        ...prev,
        [leadId]: { success: resultSummary || 'Initial email sent successfully.', error: '' },
      }));
      setLeads((prevLeads) =>
        prevLeads.map((existingLead) =>
          existingLead.id === leadId
            ? {
                ...existingLead,
                InitialEmail: draft,
                SentTo: sentTo,
                Progress: progressValue,
                LastContacted: lastContactedValue,
                Contacted: lastContactedValue,
                OfficerEmails: successfulOfficerEmails.join(', '),
              }
            : existingLead
        )
      );
      closeInitialEmailEditor(leadId);
    } catch (err) {
      console.error(err);
      setInitialEmailMessages((prev) => ({
        ...prev,
        [leadId]: { success: '', error: err?.message || 'Unable to send initial email.' },
      }));
    } finally {
      setInitialEmailSubmittingId(null);
    }
  };

  const selectReportByIndex = (index, reportsSource = availableReports) => {
    if (!Array.isArray(reportsSource) || reportsSource.length === 0) {
      return;
    }

    const maxIndex = reportsSource.length - 1;
    const safeIndex = Math.max(0, Math.min(index, maxIndex));
    const selected = reportsSource[safeIndex];
    const normalized = selected?.content ?? '';
    setSelectedReportIndex(safeIndex);
    setReportContent(normalized);
  };

  const loadExistingReports = async () => {
    setReportLoading(true);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/sales/getleadaireport', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch current AI reports');
      }

      const payload = await parseJsonOrThrow(response, '/sales/getleadaireport');
      const items = Array.isArray(payload?.data) ? payload.data : [];

      const reports = items
        .map((item, idx) => {
          const rawContent = item?.Content ?? '';
          const cleanedContent = normalizeReportValue(rawContent);

          const parsedDate = item?.ReportDate ? new Date(item.ReportDate) : null;
          const hasValidDate = parsedDate instanceof Date && !Number.isNaN(parsedDate?.getTime());

          return {
            id: item?.id ?? `report-${idx}`,
            label: hasValidDate ? format(parsedDate, 'dd/MM/yyyy') : `Report ${idx + 1}`,
            sortValue: hasValidDate ? parsedDate.getTime() : idx,
            content: cleanedContent,
          };
        })
        .sort((a, b) => a.sortValue - b.sortValue);

      setAvailableReports(reports);

      if (reports.length > 0) {
        const latestIndex = reports.length - 1;
        setCurrentReportAvailable(true);
        selectReportByIndex(latestIndex, reports);
      } else {
        setCurrentReportAvailable(false);
        setSelectedReportIndex(null);
        setReportContent(null);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error fetching current AI reports');
      setAvailableReports([]);
      setCurrentReportAvailable(false);
      setSelectedReportIndex(null);
      setReportContent(null);
    } finally {
      setReportLoading(false);
    }
  };

  const handleReportButtonClick = async () => {
    if (reportOptionsVisible) {
      setReportOptionsVisible(false);
      setCurrentReportAvailable(false);
      setAvailableReports([]);
      setSelectedReportIndex(null);
      setReportContent(null);
      return;
    }

    setReportOptionsVisible(true);
    await loadExistingReports();
  };

  const handleShowCurrentReport = () => {
    if (!availableReports.length) {
      return;
    }

    selectReportByIndex(availableReports.length - 1);
  };

  const handleGenerateNewReport = async () => {
    setNewReportLoading(true);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/sales/getaireportsales', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch new AI report');
      }

      const payload = await parseJsonOrThrow(response, '/sales/getaireportsales');
      const items = Array.isArray(payload?.data) ? payload.data : [];
      const primaryContent = items[0]?.Content ?? payload?.Content ?? '';
      const normalized = normalizeReportValue(primaryContent);
      setReportContent(normalized);
      setCurrentReportAvailable(normalized.trim().length > 0);

      if (reportOptionsVisible) {
        await loadExistingReports();
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error fetching new AI report');
    } finally {
      setNewReportLoading(false);
    }
  };

  const runAiBulkWithCredits = async () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow AI features.');
      return;
    }

    // Run unlimited AI lead-pull for all location × sector combinations
    await handleAiLeadPullLoop();
  };

  const runEnrichWithCredits = async () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow Enrich or AI features.');
      return;
    }

    // Run enrich in large batches; do not gate by local credit counters.
    await handleGenerate(500);
  };

  const creditsRemaining = Math.max(0, Number(salesCredits) || 0);
  const noCreditsRemaining = creditsRemaining <= 0;
  const selectedEnrichCredits = Math.max(0, Number(enrichCreditsToUse) || 0);
  const canRunEnrich =
    selectedEnrichCredits >= 0 &&
    enrichPreview.leadsToEnrich > 0;

  const openSmtpModal = async () => {
    setSmtpError('');
    setSmtpFeedback('');
    setSmtpModalOpen(true);
    await fetchSmtpSettings();
  };

  const openSettingsModal = async () => {
    setSettingsError('');
    setSettingsFeedback('');
    setPasswordError('');
    setPasswordFeedback('');
    setSettingsModalOpen(true);
    await fetchCompanyCredits();
  };

  const handleCompanyFieldChange = (field, value) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveCompanyProfile = async () => {
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsFeedback('');
    try {
      const response = await fetch('https://api.insightos.co.uk/sales/editcompany', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(companyForm),
      });

      const payload = await parseJsonOrThrow(response, '/sales/editcompany');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Unable to update company profile');
      }

      setSettingsFeedback('Company details updated.');
      await fetchCompanyCredits();
    } catch (err) {
      console.error(err);
      setSettingsError(err.message || 'Unable to update company details.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const savePasswordChange = async () => {
    setPasswordError('');
    setPasswordFeedback('');

    if (!passwordOne || !passwordTwo) {
      setPasswordError('Please enter both password fields.');
      return;
    }

    if (passwordOne !== passwordTwo) {
      setPasswordError('Passwords must match.');
      return;
    }

    setPasswordSaving(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/sales/passwordchange', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: passwordOne, confirmPassword: passwordTwo }),
      });

      const payload = await parseJsonOrThrow(response, '/sales/passwordchange');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Unable to change password');
      }

      setPasswordFeedback('Password changed successfully.');
      setPasswordOne('');
      setPasswordTwo('');
    } catch (err) {
      console.error(err);
      setPasswordError(err.message || 'Unable to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSmtpFieldChange = (field, value) => {
    setSmtpForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSmtpProviderChange = (providerKey) => {
    setSmtpProvider(providerKey);
    const preset = smtpProviderPresets[providerKey] || smtpProviderPresets.custom;
    if (providerKey === 'custom') return;

    setSmtpForm((prev) => ({
      ...prev,
      host: preset.host,
      port: preset.port,
      secure: preset.secure,
    }));
  };

  const saveSmtpSettings = async () => {
    if (!smtpForm.host.trim() || !smtpForm.port.trim() || !smtpForm.username.trim() || !smtpForm.password.trim()) {
      setSmtpError('Please complete SMTP host, port, username and password.');
      setSmtpFeedback('');
      return;
    }

    try {
      setSmtpSaving(true);
      const response = await fetch('https://api.insightos.co.uk/sales/smptp', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(smtpForm),
      });

      const payload = await parseJsonOrThrow(response, '/sales/smptp');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Unable to save SMTP settings');
      }

      await checkSmtpConnection(smtpForm);
      setSmtpFeedback('SMTP settings saved to CompanySales.');
      setSmtpError('');
    } catch (err) {
      console.error(err);
      setSmtpError(err.message || 'Unable to save SMTP data.');
      setSmtpFeedback('');
    } finally {
      setSmtpSaving(false);
    }
  };

  const glassRootClass = 'relative min-h-screen bg-cover bg-center bg-no-repeat px-2 pb-8 pt-3 sm:px-4';
  const glassRootStyle = {
    backgroundImage:
      "linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(233,213,255,0.82) 45%, rgba(255,255,255,0.92) 100%), url('/avenorlogobackground.png')",
  };
  const glassShellClass = 'rounded-2xl border border-white/50 bg-white/55 shadow-[0_20px_70px_rgba(59,130,246,0.18)] backdrop-blur-xl';
  const glassSectionClass = 'border-b border-white/45 bg-white/45 backdrop-blur-xl';
  const glassCardClass = 'rounded-xl border border-white/50 bg-white/70 shadow-[0_10px_35px_rgba(76,29,149,0.14)] backdrop-blur';

  const scrollToSection = (sectionRef) => {
    if (!sectionRef?.current) return;
    sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
  <div className={glassRootClass} style={glassRootStyle}>
  <div className="sticky top-0 z-40 rounded-2xl border border-white/50 bg-white/65 shadow-[0_12px_35px_rgba(2,132,199,0.25)] backdrop-blur-xl">
    <div className="mx-auto flex w-full max-w-7xl items-center justify-end px-3 py-3 sm:px-6">
      <div className="w-full sm:w-auto">
        <button
          type="button"
          onClick={openSmtpModal}
          className="w-full rounded-lg bg-gradient-to-r from-sky-600 to-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-sky-500/30 hover:from-sky-500 hover:to-blue-500 sm:w-auto"
        >
          SMPT
        </button>
      </div>
    </div>
  </div>
  {filterIsSticky && showLeadActions && (
    <div className="fixed left-0 right-0 top-14 z-[45] border-b border-white/60 bg-white/90 px-3 py-2 shadow-lg backdrop-blur-xl sm:top-16 sm:px-6">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-600">Quick Filters</span>
        <select
          value={locationSelectValue}
          onChange={(e) => setLocation(e.target.value)}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="">All Locations</option>
          {locationOptionsForActiveFilters.map((loc) => (
            <option key={`sticky-loc-${loc}`} value={loc}>{loc}</option>
          ))}
        </select>
        <select
          value={sectorSelectValue}
          onChange={(e) => setSector(e.target.value)}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="">All Sectors</option>
          {sectorOptionsForActiveFilters.map((sec) => (
            <option key={`sticky-sec-${sec}`} value={sec}>{sec}</option>
          ))}
        </select>
        <button
          onClick={() => setShowFilteredOnly((prev) => !prev)}
          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${showFilteredOnly ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'}`}
        >
          {showFilteredOnly ? '✓ Enriched' : 'Enriched'}
        </button>
        <button
          onClick={() => setShowCompaniesHouseHitOnly((prev) => !prev)}
          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${showCompaniesHouseHitOnly ? 'bg-sky-600 text-white' : 'bg-sky-100 text-sky-800 hover:bg-sky-200'}`}
        >
          {showCompaniesHouseHitOnly ? '✓ CH Hit' : 'CH Hit'}
        </button>
        <button
          onClick={() => setShowLatestReplyPendingOnly((prev) => !prev)}
          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${showLatestReplyPendingOnly ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
        >
          {showLatestReplyPendingOnly ? '✓ Has Latest Reply' : 'Has Latest Reply'}
        </button>
        <input
          type="text"
          value={globalLeadSearch}
          onChange={(e) => setGlobalLeadSearch(e.target.value)}
          placeholder="Search leads..."
          className="w-32 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
        <span className="ml-auto text-xs font-semibold text-slate-600">{filteredLeads.length} leads</span>
      </div>
    </div>
  )}
  {pendingLatestReplyCount > 0 && (
    <div className="mx-3 mt-3 rounded-xl border border-amber-300 bg-amber-50/90 px-4 py-3 text-sm text-amber-900 shadow-sm sm:mx-6">
      <span className="font-semibold">Heads up:</span> {pendingLatestReplyCount} lead{pendingLatestReplyCount === 1 ? '' : 's'} currently have a `LatestReply` value.
    </div>
  )}
  <div className={classNames('mt-3 w-full', glassShellClass)}>
      <div className={classNames(glassSectionClass, 'px-3 py-3 sm:px-6')}>
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:items-center sm:overflow-visible sm:pb-0">
          <span className="mr-2 text-xs font-semibold uppercase tracking-wide text-slate-700">Quick Jump</span>
          <button
            type="button"
            onClick={() => scrollToSection(leadActionsRef)}
            className="shrink-0 rounded-lg border border-sky-200/70 bg-sky-50/70 px-3 py-1.5 text-xs font-semibold text-sky-800 shadow-sm hover:bg-sky-100/80"
          >
            Lead Actions
          </button>
          <button
            type="button"
            onClick={() => scrollToSection(reportsRef)}
            className="shrink-0 rounded-lg border border-blue-200/70 bg-blue-50/70 px-3 py-1.5 text-xs font-semibold text-blue-800 shadow-sm hover:bg-blue-100/80"
          >
            Reports
          </button>
          <button
            type="button"
            onClick={() => scrollToSection(bulkOpsRef)}
            className="shrink-0 rounded-lg border border-emerald-200/70 bg-emerald-50/70 px-3 py-1.5 text-xs font-semibold text-emerald-800 shadow-sm hover:bg-emerald-100/80"
          >
            Bulk Ops
          </button>
          <button
            type="button"
            onClick={() => scrollToSection(leadsTableRef)}
            className="shrink-0 rounded-lg border border-purple-200/70 bg-purple-50/70 px-3 py-1.5 text-xs font-semibold text-purple-800 shadow-sm hover:bg-purple-100/80"
          >
            Leads Table
          </button>
        </div>
      </div>
      {/* Dashboard Stats */}
      <div className="border-b border-white/45 bg-gradient-to-br from-purple-100/60 via-sky-100/50 to-emerald-100/60 px-3 py-5 backdrop-blur sm:px-6 sm:py-6">
        <h2 className="mb-4 text-lg font-bold text-gray-900 sm:text-xl">Lead Dashboard</h2>
        
        {/* Key Metrics Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className={classNames(glassCardClass, 'border-purple-200/80 p-4')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total Leads</p>
                <p className="mt-1 text-3xl font-bold text-indigo-600">{dashboardStats.totalLeads}</p>
              </div>
              <div className="rounded-full bg-indigo-100 p-3">
                <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          

          <div className={classNames(glassCardClass, 'border-blue-200/80 p-4')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Initial Emails</p>
                <p className="mt-1 text-3xl font-bold text-blue-600">{dashboardStats.leadsWithInitialEmails}</p>
                <p className="mt-1 text-xs text-gray-500">Drafted</p>
              </div>
              <div className="rounded-full bg-blue-100 p-3">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Metrics */}
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className={classNames(glassCardClass, 'p-3')}>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Enriched</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{dashboardStats.filteredCount}</p>
          </div>
          <div className={classNames(glassCardClass, 'p-3')}>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Contacted</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{dashboardStats.leadsContacted}</p>
          </div>
          <div className={classNames(glassCardClass, 'p-3')}>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Responded</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{dashboardStats.leadsResponded}</p>
          </div>
        </div>

        {/* Companies House */}
        <div className="mt-4 grid gap-4 sm:grid-cols-1">
          <div className={classNames(glassCardClass, 'border-sky-200/80 p-4')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Companies House Hit</p>
                <p className="mt-1 text-3xl font-bold text-sky-600">{dashboardStats.companiesHouseHitCount}</p>
                <p className="mt-1 text-xs text-gray-500">{dashboardStats.companiesHousePercentage}% of total</p>
              </div>
              <div className="rounded-full bg-sky-100 p-3">
                <svg className="h-6 w-6 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Top Locations and Sectors */}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Top 5 Locations</h3>
            <div className="space-y-2">
              {dashboardStats.topLocations.length > 0 ? (
                dashboardStats.topLocations.map(([location, count]) => (
                  <div key={location} className="flex items-center justify-between gap-2">
                    <span className="max-w-[8.5rem] truncate text-xs text-gray-700 sm:max-w-none sm:text-sm">{location}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 sm:w-24">
                        <div
                          className="h-full bg-indigo-500"
                          style={{ width: `${(count / dashboardStats.totalLeads) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-gray-900 sm:text-sm">{count}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No location data</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Top 5 Sectors</h3>
            <div className="space-y-2">
              {dashboardStats.topSectors.length > 0 ? (
                dashboardStats.topSectors.map(([sector, count]) => (
                  <div key={sector} className="flex items-center justify-between gap-2">
                    <span className="max-w-[8.5rem] truncate text-xs text-gray-700 sm:max-w-none sm:text-sm">{sector}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 sm:w-24">
                        <div
                          className="h-full bg-purple-500"
                          style={{ width: `${(count / dashboardStats.totalLeads) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-gray-900 sm:text-sm">{count}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No sector data</p>
              )}
            </div>
          </div>
        </div>

      </div>

      <div ref={filterSentinelRef} className="h-0 overflow-hidden" />
      {/* Lead Actions Section */}
      <div ref={leadActionsRef} className={glassSectionClass}>
        <button
          onClick={() => setShowLeadActions((prev) => !prev)}
          className="flex w-full items-start justify-between gap-3 bg-gradient-to-r from-purple-100/60 via-sky-100/50 to-emerald-100/60 px-3 py-3 text-left hover:from-purple-100/80 hover:to-emerald-100/80 sm:items-center sm:px-6"
        >
          <div className="flex items-center gap-3">
            <svg
              className={`h-5 w-5 text-slate-700 transition-transform ${showLeadActions ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <h3 className="text-sm font-semibold text-slate-900 sm:text-base">Lead Actions & Filters</h3>
          </div>
          <span className="shrink-0 rounded-full bg-purple-100/90 px-2.5 py-1 text-[11px] font-semibold text-purple-800 sm:px-3 sm:text-xs">
            {filteredLeads.length} shown
          </span>
        </button>

        {showLeadActions && (
          <div className="space-y-4 bg-white/35 px-4 py-4 backdrop-blur sm:px-6">
            <div className="rounded-2xl border border-sky-200/70 bg-sky-100/45 p-4 shadow-inner backdrop-blur">
              <div className="mb-3">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-sky-900">
                  Manual Workflow
                </label>
                <p className="text-xs text-sky-800">
                  Use manual filters and run a standard lead pull.
                </p>
              </div>
              <div className="mb-3">
                <button
                  onClick={handleLeadPull}
                  className="w-full rounded-lg bg-gradient-to-r from-blue-600 to-sky-600 px-4 py-2 font-semibold text-white shadow-lg shadow-blue-500/30 hover:from-blue-500 hover:to-sky-500 focus:outline-none focus:ring-2 focus:ring-blue-400 sm:w-auto"
                  disabled={actionsDisabled || deletingLeadId !== null}
                >
                  {leadPullLoading ? 'Processing...' : 'New Lead Pull (Free)'}
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  value={location}
                  onChange={(event) => setLocation(event.target.value)}
                  placeholder="Location"
                  className="w-full rounded-lg border border-sky-200/70 bg-white/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 sm:w-auto"
                />

                <select
                  value={locationSelectValue}
                  onChange={(event) => setLocation(event.target.value)}
                  className="w-full rounded-lg border border-sky-200/70 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 sm:w-auto"
                >
                  <option value="">All Locations</option>
                  {availableLocations.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>

                <input
                  type="text"
                  value={sector}
                  onChange={(event) => setSector(event.target.value)}
                  placeholder="Sector"
                  className="w-full rounded-lg border border-sky-200/70 bg-white/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 sm:w-auto"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-purple-900">
                AI Workflow
              </label>
              {isPermissionRestricted && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  AI and Enrich features are disabled for your permission level.
                </div>
              )}
              <p className="mb-3 text-xs text-purple-800">
                Use AI to generate towns, sectors, and pull matching leads.
              </p>
              <div className="grid gap-3 rounded-xl border border-purple-200/70 bg-purple-100/45 p-3 shadow-inner backdrop-blur sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-purple-900">AI towns / areas prompt</label>
                  <p className="text-[11px] text-purple-700">Generate town/area suggestions from AI.</p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={aiLocationPrompt}
                      onChange={(event) => setAiLocationPrompt(event.target.value)}
                      placeholder="e.g. towns in Kent UK"
                      className="flex-1 rounded-lg border border-purple-200/70 bg-white/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={handleAiLocationsRequest}
                      className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-purple-500/25 hover:from-purple-500 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:cursor-not-allowed sm:w-auto"
                      disabled={aiLocationsLoading || actionsDisabled || !canUseAiAndEnrich}
                    >
                      {aiLocationsLoading ? 'Pulling...' : 'AI Town Pull'}
                    </button>
                  </div>
                  {aiLocations.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {aiLocations.map((loc) => (
                        <span
                          key={`ai-loc-${loc}`}
                          className="inline-flex items-center gap-1 rounded-full border border-purple-200/70 bg-white/80 px-3 py-1 text-xs font-semibold text-purple-800 shadow"
                        >
                          {loc}
                          <button
                            type="button"
                            onClick={() => removeAiLocation(loc)}
                            className="text-purple-500 hover:text-red-600"
                            aria-label={`Remove ${loc}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <button
                        type="button"
                        onClick={() => setAiLocations([])}
                        className="text-xs font-semibold text-red-600 hover:text-red-800"
                      >
                        Clear towns
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-purple-900">AI sectors prompt</label>
                  <p className="text-[11px] text-purple-700">Generate sector suggestions from AI.</p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={aiSectorPrompt}
                      onChange={(event) => setAiSectorPrompt(event.target.value)}
                      placeholder="e.g. service sectors in construction"
                      className="flex-1 rounded-lg border border-purple-200/70 bg-white/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={handleAiSectorsRequest}
                      className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-purple-500/25 hover:from-purple-500 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:cursor-not-allowed sm:w-auto"
                      disabled={aiSectorsLoading || actionsDisabled || !canUseAiAndEnrich}
                    >
                      {aiSectorsLoading ? 'Pulling...' : 'AI Sector Pull'}
                    </button>
                  </div>
                  {aiSectors.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {aiSectors.map((sec) => (
                        <span
                          key={`ai-sec-${sec}`}
                          className="inline-flex items-center gap-1 rounded-full border border-purple-200/70 bg-white/80 px-3 py-1 text-xs font-semibold text-purple-800 shadow"
                        >
                          {sec}
                          <button
                            type="button"
                            onClick={() => removeAiSector(sec)}
                            className="text-purple-500 hover:text-red-600"
                            aria-label={`Remove ${sec}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <button
                        type="button"
                        onClick={() => setAiSectors([])}
                        className="text-xs font-semibold text-red-600 hover:text-red-800"
                      >
                        Clear sectors
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-purple-200/70 bg-purple-100/55 p-3 shadow-inner backdrop-blur">
                <div className="mb-2">
                  <label className="text-xs font-semibold text-purple-900">AI lead pull</label>
                </div>
                <p className="mb-3 text-[11px] text-purple-700">
                  This runs lead pulls using your current AI towns and AI sectors list.
                </p>
                <button
                  onClick={runAiBulkWithCredits}
                  className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-sky-600 px-4 py-2 font-semibold text-white shadow-lg shadow-purple-500/30 hover:from-purple-500 hover:to-sky-500 focus:outline-none focus:ring-2 focus:ring-purple-400 sm:w-auto"
                  disabled={actionsDisabled || aiLeadPullRunning || deletingLeadId !== null || !canUseAiAndEnrich}
                >
                  {aiLeadPullRunning ? 'Running AI Pull Leads...' : 'AI Pull Leads'}
                </button>
              </div>

              {aiLeadPullProgress && (
                <div className="mt-3 rounded-lg border border-purple-200/80 bg-purple-100/60 px-3 py-2 text-xs text-purple-800 shadow-sm sm:text-sm">
                  {aiLeadPullProgress}
                </div>
              )}
            </div>

            {/* ── Filters & Enrich ─────────────────────────────────── */}
            <div className="space-y-3">

              {/* Row 1: Search + Location + Sector */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <input
                  type="text"
                  value={globalLeadSearch}
                  onChange={(e) => setGlobalLeadSearch(e.target.value)}
                  placeholder="Search all lead fields..."
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <select
                  value={locationSelectValue}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full rounded-lg border border-sky-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                >
                  <option value="">All Locations</option>
                  {locationOptionsForActiveFilters.map((loc) => (
                    <option key={`active-filter-loc-${loc}`} value={loc}>{loc}</option>
                  ))}
                </select>
                <select
                  value={sectorSelectValue}
                  onChange={(e) => setSector(e.target.value)}
                  className="w-full rounded-lg border border-purple-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="">All Sectors</option>
                  {sectorOptionsForActiveFilters.map((sec) => (
                    <option key={`active-filter-sec-${sec}`} value={sec}>{sec}</option>
                  ))}
                </select>
              </div>

              {/* Row 2: Toggle filters + clear buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowFilteredOnly((prev) => !prev)}
                  disabled={actionsDisabled || deletingLeadId !== null}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    showFilteredOnly ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50'
                  }`}
                >
                  {showFilteredOnly ? '✓ Enriched Only' : 'Enriched Only'}
                </button>
                <button
                  onClick={() => setShowCompaniesHouseHitOnly((prev) => !prev)}
                  disabled={actionsDisabled || deletingLeadId !== null}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    showCompaniesHouseHitOnly ? 'border-sky-600 bg-sky-600 text-white' : 'border-sky-300 bg-white text-sky-700 hover:bg-sky-50'
                  }`}
                >
                  {showCompaniesHouseHitOnly ? '✓ CH Hit Only' : 'CH Hit Only'}
                </button>
                <button
                  onClick={() => setShowContactedOnly((prev) => !prev)}
                  disabled={actionsDisabled || deletingLeadId !== null}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    showContactedOnly ? 'border-amber-500 bg-amber-500 text-white' : 'border-amber-300 bg-white text-amber-700 hover:bg-amber-50'
                  }`}
                >
                  {showContactedOnly ? '✓ Contacted' : 'Contacted Only'}
                </button>
                <button
                  onClick={() => setShowLatestReplyPendingOnly((prev) => !prev)}
                  disabled={actionsDisabled || deletingLeadId !== null}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    showLatestReplyPendingOnly ? 'border-amber-600 bg-amber-600 text-white' : 'border-amber-300 bg-white text-amber-700 hover:bg-amber-50'
                  }`}
                >
                  {showLatestReplyPendingOnly ? '✓ Has Latest Reply' : 'Has Latest Reply'}
                </button>
                <div className="ml-auto flex flex-wrap gap-2">
                  <button type="button" onClick={() => setLocation('')} className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Clear Location</button>
                  <button type="button" onClick={() => setSector('')} className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Clear Sector</button>
                  <button type="button" onClick={() => { setShowContactedOnly(false); setShowLatestReplyPendingOnly(false); setContactedFromDate(''); setContactedToDate(''); setGlobalLeadSearch(''); }} className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Clear All</button>
                </div>
              </div>

              {/* Row 3: Date range — only shown when relevant */}
              {(showContactedOnly || contactedFromDate || contactedToDate) && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2">
                  <span className="text-xs font-semibold text-amber-800">Contact Date Range</span>
                  <input type="date" value={contactedFromDate} onChange={(e) => setContactedFromDate(e.target.value)} className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400" aria-label="Contacted from" />
                  <span className="text-xs text-amber-600">to</span>
                  <input type="date" value={contactedToDate} onChange={(e) => setContactedToDate(e.target.value)} className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400" aria-label="Contacted to" />
                  <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900">{contactedInDateRangeCount} in range</span>
                </div>
              )}

              {/* Enrich — linked to active location/sector filter */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 shadow-inner backdrop-blur">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-bold text-emerald-800">Enrich Leads</span>
                  <span className="text-xs text-emerald-600">Filtered batch processing</span>
                </div>
                {/* Active filter context */}
                <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                  {location.trim() && (
                    <span className="flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 font-semibold text-sky-800">
                      📍 {location.trim()}
                      <button type="button" onClick={() => setLocation('')} className="ml-0.5 text-sky-500 hover:text-red-500">×</button>
                    </span>
                  )}
                  {sector.trim() && (
                    <span className="flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 font-semibold text-purple-800">
                      🏢 {sector.trim()}
                      <button type="button" onClick={() => setSector('')} className="ml-0.5 text-purple-500 hover:text-red-500">×</button>
                    </span>
                  )}
                  {!location.trim() && !sector.trim() && (
                    <span className="text-slate-500">No location/sector filter — enriching all leads in scope</span>
                  )}
                </div>
                <div className="mb-3 grid grid-cols-2 gap-2 rounded-lg border border-white/60 bg-white/60 px-3 py-2 text-xs sm:flex sm:flex-wrap sm:gap-4">
                  <div><span className="font-semibold text-slate-700">Leads in scope:</span> <strong className="text-slate-900">{enrichPreview.targetCount}</strong></div>
                  <div><span className="font-semibold text-slate-700">Can enrich now:</span> <strong className="text-slate-900">{enrichPreview.leadsToEnrich}</strong></div>
                  <div><span className="font-semibold text-slate-700">Batch size:</span> <strong className="text-slate-900">{enrichPreview.selectedCredits}</strong></div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs font-semibold text-emerald-800">Batch size</label>
                    <input
                      type="number"
                      min="2"
                      step="1"
                      value={enrichCreditsToUse}
                      onChange={(e) => setEnrichCreditsToUse(e.target.value)}
                      className="w-20 rounded-lg border border-emerald-300 bg-white px-2 py-1 text-sm"
                    />
                  </div>
                  <button
                    onClick={runEnrichWithCredits}
                    disabled={actionsDisabled || deletingLeadId !== null || !canRunEnrich || !canUseAiAndEnrich}
                    className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 hover:from-emerald-500 hover:to-sky-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {filterLoading ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                        Enriching...
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Enrich {enrichPreview.leadsToEnrich > 0 ? `${enrichPreview.leadsToEnrich} leads` : '(no leads)'}{(location.trim() || sector.trim()) ? ' · filtered' : ''}
                      </>
                    )}
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-slate-500">Applies to leads in the current filter scope.</p>
              </div>

            </div>
          </div>
        )}
      </div>

      {/* Reports Section */}
      <div ref={reportsRef} className={glassSectionClass}>
        <div className="bg-white/35 px-4 py-3 backdrop-blur sm:px-6">
          <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
            <button
              onClick={handleReportButtonClick}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-sky-600 to-blue-600 px-4 py-2 font-semibold text-white shadow-lg shadow-sky-500/25 hover:from-sky-500 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-sky-400 sm:w-auto"
              disabled={actionsDisabled || deletingLeadId !== null || reportLoading || newReportLoading || !canUseAiAndEnrich}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {reportLoading ? 'Loading...' : reportOptionsVisible ? 'Hide Reports' : 'AI Reports'}
            </button>

          {reportOptionsVisible && (
            <>
              <button
                onClick={handleGenerateNewReport}
                className="w-full rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 font-semibold text-white shadow-lg shadow-blue-500/25 hover:from-blue-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-blue-400 sm:w-auto"
                disabled={actionsDisabled || deletingLeadId !== null || reportLoading || newReportLoading}
              >
                {newReportLoading ? 'Generating...' : 'New Report'}
              </button>

              {currentReportAvailable && (
                <button
                  onClick={handleShowCurrentReport}
                  className="w-full rounded-lg border border-white/60 bg-white/80 px-4 py-2 font-semibold text-slate-700 shadow-sm hover:bg-white sm:w-auto"
                  disabled={actionsDisabled || deletingLeadId !== null || reportLoading || newReportLoading}
                >
                  Current
                </button>
              )}
            </>
          )}
        </div>
        </div>
      </div>

      {/* Bulk Operations Section */}
      <div ref={bulkOpsRef} className={glassSectionClass}>
        <button
          onClick={() => setShowBulkOperations((prev) => !prev)}
          className="flex w-full items-center justify-between bg-gradient-to-r from-emerald-100/60 via-sky-100/50 to-purple-100/60 px-4 py-3 text-left hover:from-emerald-100/80 hover:to-purple-100/80 sm:px-6"
        >
          <div className="flex items-center gap-3">
            <svg
              className={`h-5 w-5 text-gray-600 transition-transform ${showBulkOperations ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <h3 className="text-base font-semibold text-slate-900">Bulk Operations</h3>
          </div>
        </button>

        {showBulkOperations && (
          <div className="space-y-3 bg-white/35 px-4 py-4 backdrop-blur sm:px-6">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={bulkApplyFilteredOnly}
                onChange={(event) => setBulkApplyFilteredOnly(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Apply to currently filtered leads only ({filteredLeads.length} leads)
            </label>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={openGranularSendModal}
                className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 hover:from-emerald-500 hover:to-sky-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:bg-emerald-400"
                disabled={actionsDisabled}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send by Sector/Location
              </button>

              <button
                type="button"
                onClick={handleBulkDeleteLeads}
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:bg-red-400"
                disabled={bulkDeleteRunning || actionsDisabled}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {bulkDeleteRunning ? 'Deleting...' : 'Delete selected leads'}
              </button>
            </div>

            {bulkInitialSendFeedback && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {bulkInitialSendFeedback}
              </div>
            )}
            {bulkInitialSendError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {bulkInitialSendError}
              </div>
            )}

            {bulkDeleteFeedback && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {bulkDeleteFeedback}
              </div>
            )}
            {bulkDeleteError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {bulkDeleteError}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {editingLead && (
        <div className="border-y border-indigo-100 bg-indigo-50/60 px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-base font-semibold text-indigo-900 sm:text-lg">Edit Initial Email</h3>
                <p className="text-xs text-indigo-700 sm:text-sm">
                  {editingLead.LeadName ?? 'Selected Lead'}
                  {editingLead.Company ? ` - ${editingLead.Company}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => closeInitialEmailEditor(editingLead.id, { resetMessage: true })}
                className="self-start rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                disabled={editingSubmitting}
              >
                Close Editor
              </button>
            </div>

            <div className="rounded-xl border border-indigo-200 bg-white p-3 shadow-inner sm:p-4">
              <textarea
                value={editingDraft}
                onChange={(event) => handleInitialEmailDraftChange(editingLead.id, event.target.value)}
                rows={10}
                className="w-full resize-y rounded-md border border-slate-300 px-3 py-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="Compose the initial email..."
                disabled={editingSubmitting}
              />
            </div>

            {editingMessage?.error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 sm:text-sm">
                {editingMessage.error}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => submitInitialEmail(editingLead)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-400 sm:w-auto"
                disabled={editingSubmitting}
              >
                {editingSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-5 w-5" />
                    Send Initial Email
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => closeInitialEmailEditor(editingLead.id, { resetMessage: true })}
                className="w-full rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed sm:w-auto"
                disabled={editingSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {sectorSummary && (
        <div className="px-4 sm:px-6 py-3 bg-indigo-50 border-b border-indigo-100 text-sm text-indigo-800">
          <span className="font-semibold mr-2">Pursued Leads by Sector:</span>
          {Object.keys(sectorSummary).length === 0 ? (
            <span>No pursued leads available.</span>
          ) : (
            <span>
              {Object.entries(sectorSummary).map(([sectorName, count], idx) => (
                <span key={sectorName}>
                  {idx > 0 && ', '}
                  <span className="font-medium">{sectorName}</span>: {count}
                </span>
              ))}
            </span>
          )}
        </div>
      )}

      {reportOptionsVisible && (
        <div className="border-b border-white/45 bg-white/35 px-4 py-4 text-sm text-slate-700 backdrop-blur sm:px-6">
          {availableReports.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div className="font-semibold text-slate-900">AI Sales Report</div>
              <div className="flex flex-wrap gap-2">
                {availableReports.map((report, idx) => {
                  const isActive = idx === selectedReportIndex;
                  return (
                    <button
                      type="button"
                      key={report.id ?? `${report.rawDate ?? idx}`}
                      onClick={() => selectReportByIndex(idx)}
                      className={`rounded-md px-3 py-1.5 text-sm font-medium border transition-colors ${
                        isActive
                            ? 'border-purple-600 bg-purple-600 text-white shadow-sm'
                            : 'border-white/70 bg-white/80 text-slate-700 hover:bg-white'
                      }`}
                    >
                      {report.formattedDate}
                    </button>
                  );
                })}
              </div>
              <div className="max-h-64 overflow-y-auto rounded-lg border border-white/70 bg-white/75 p-3 text-xs whitespace-pre-wrap shadow-inner sm:text-sm">
                {typeof reportContent === 'string' && reportContent.trim().length > 0
                  ? reportContent
                  : 'No report content available for this date.'}
              </div>
            </div>
          ) : reportLoading ? (
            <div className="text-sm text-slate-600">Loading reports...</div>
          ) : (
            <div className="text-sm text-slate-600">No reports available.</div>
          )}
        </div>
      )}

      <div ref={leadsTableRef} className="bg-white/30 backdrop-blur">
        {/* ── Mobile card view (hidden on md+) ──────────────────── */}
        <div className="p-3 space-y-3 md:hidden">
          {tableLoading ? (
            <p className="py-8 text-center text-sm text-slate-500">Loading leads...</p>
          ) : filteredLeads.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No leads found.</p>
          ) : (
            visibleFilteredLeads.map((lead, index) => {
              const rowId = lead.id ?? `${lead.LeadName ?? 'lead'}-${lead.Email ?? index}`;
              const companiesHouseHit = Number(lead.CompaniesHouseHit ?? lead.companieshousehit ?? 0) === 1;
              const filterValue = Number(lead.Filter ?? 0);
              const callScriptText = String(lead.CallScript ?? lead.callScript ?? '').trim();
              const hasCallScript = callScriptText.length > 0;
              const isContactedLead = Boolean(parseLeadContactDate(lead));
              const isSingleEnrichRunning = singleLeadEnrichingId === lead.id;
              const isExpanded = expandedRows.has(rowId);
              const companyReportText = lead.CompanyReport ?? '';
              return (
                <div
                  key={rowId}
                  className={`rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur transition ${
                    isContactedLead ? 'border-amber-300' : companiesHouseHit ? 'border-emerald-300' : 'border-slate-200/80'
                  }`}
                >
                  {/* Card header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {isContactedLead && <span className="text-base text-amber-500" title="Contacted">★</span>}
                        <span className="truncate text-sm font-bold text-slate-900">{lead.LeadName ?? '-'}</span>
                        {companiesHouseHit && (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">CH Hit</span>
                        )}
                        {filterValue === 1 && (
                          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-800">Enriched</span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-slate-600">{lead.Company ?? '-'}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {lead.Sector && (
                          <span className="rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700">{lead.Sector}</span>
                        )}
                        {lead.Location && (
                          <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700">{lead.Location}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleRowExpansion(rowId)}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </button>
                  </div>

                  {/* Status row */}
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                    <span className="flex items-center gap-1">
                      <span className="font-semibold">Progress:</span>
                      <select
                        value={['No Reply', 'Interested', 'Sale'].includes(String(lead?.Progress || '').trim()) ? String(lead.Progress).trim() : 'No Reply'}
                        onChange={(e) => handleProgressChange(lead.id, e.target.value)}
                        disabled={!lead.id || progressSavingLeadId === lead.id}
                        className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs"
                      >
                        <option value="No Reply">No Reply</option>
                        <option value="Interested">Interested</option>
                        <option value="Sale">Sale</option>
                      </select>
                    </span>
                    {isContactedLead && (
                      <span><span className="font-semibold">Contacted:</span> {lead?.LastContacted ? new Date(lead.LastContacted).toLocaleDateString() : '-'}</span>
                    )}
                  </div>
                  {lead.SentTo && (
                    <p className="mt-1 text-xs text-slate-500"><span className="font-semibold">Sent to:</span> {lead.SentTo}</p>
                  )}

                  {/* Email preview */}
                  {lead.InitialEmail && (
                    <div className="mt-2 line-clamp-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600">
                      {lead.InitialEmail}
                    </div>
                  )}
                  {initialEmailMessages[lead.id]?.success && (
                    <div className="mt-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs text-emerald-700">{initialEmailMessages[lead.id].success}</div>
                  )}
                  {initialEmailMessages[lead.id]?.error && (
                    <div className="mt-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-600">{initialEmailMessages[lead.id].error}</div>
                  )}

                  {/* Actions */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {initialEmailEditingId !== lead.id && lead.id && (
                      <button
                        type="button"
                        onClick={() => openInitialEmailEditor(lead)}
                        disabled={deletingLeadId !== null || !lead.id}
                        className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                      >
                        Edit Email
                      </button>
                    )}
                    {filterValue !== 1 && lead.id && (
                      <button
                        type="button"
                        onClick={() => handleSingleLeadEnrich(lead)}
                        disabled={deletingLeadId !== null || singleLeadEnrichingId !== null}
                        className="rounded-md bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
                      >
                        {isSingleEnrichRunning ? 'Enriching...' : 'Enrich'}
                      </button>
                    )}
                    {!hasCallScript && (
                      <button
                        type="button"
                        onClick={() => handleGenerateCallScript(lead)}
                        disabled={deletingLeadId !== null || singleLeadEnrichingId !== null || callScriptGeneratingId !== null || !canUseAiAndEnrich}
                        className="rounded-md bg-gradient-to-r from-emerald-600 to-sky-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
                      >
                        {callScriptGeneratingId === lead.id ? 'Generating...' : 'Script'}
                      </button>
                    )}
                    {lead.id && (
                      <button
                        type="button"
                        onClick={() => handleDeleteLead(lead.id)}
                        disabled={deletingLeadId !== null || singleLeadEnrichingId !== null}
                        className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
                      >
                        {deletingLeadId === lead.id ? 'Deleting...' : 'Delete'}
                      </button>
                    )}
                    {hasInsightAccount && isContactedLead && lead.id && (
                      <button
                        type="button"
                        onClick={() => handleAddToCrm(lead)}
                        disabled={crmAddingLeadId === lead.id}
                        className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600"
                      >
                        {crmAddingLeadId === lead.id ? 'Continuing...' : 'Continue'}
                      </button>
                    )}
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="mt-3 space-y-2 border-t border-slate-200 pt-3 text-xs text-slate-700">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div><span className="font-semibold">Email:</span> <span className="break-all">{lead.Email || '-'}</span></div>
                        <div><span className="font-semibold">Tel:</span> {lead.Telephone || '-'}</div>
                        <div className="col-span-2"><span className="font-semibold">Address:</span> {lead.Address || '-'}</div>
                        <div><span className="font-semibold">Co. No:</span> {lead.CompanyNumber || '-'}</div>
                        <div><span className="font-semibold">Co. Size:</span> {lead.CompanySize || '-'}</div>
                        <div><span className="font-semibold">CH Hit:</span> {companiesHouseHit ? 'Yes' : 'No'}</div>
                        <div><span className="font-semibold">Occupation:</span> {lead.Occupation || '-'}</div>
                      </div>
                      {lead.Website && (
                        <div><span className="font-semibold">Website:</span> <a href={lead.Website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline break-all">{lead.Website}</a></div>
                      )}
                      {companyReportText ? (
                        <div>
                          <span className="font-semibold">Company Report:</span>
                          <span className="ml-1 line-clamp-3">{companyReportText.slice(0, 200)}{companyReportText.length > 200 ? '...' : ''}</span>
                          {companyReportText.length > 100 && (
                            <button type="button" onClick={() => openModal('Company Report', companyReportText)} className="ml-1 font-semibold text-indigo-600 hover:underline">View</button>
                          )}
                        </div>
                      ) : null}
                      {hasCallScript && (
                        <div>
                          <span className="font-semibold">Call Script:</span>
                          <button type="button" onClick={() => openModal('Call Script', callScriptText)} className="ml-1 font-semibold text-indigo-600 hover:underline">View Script</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ── Desktop table view (hidden on mobile) ────────────────── */}
        <div className="hidden md:block overflow-x-auto">
        <table className="min-w-[1080px] divide-y divide-white/50 text-xs sm:min-w-full sm:text-sm">
          <thead className="bg-white/60">
            <tr>
              <th className="w-10 px-2 py-2"></th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Lead Name</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Company</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Sector</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">
                <button
                  onClick={() => {
                    setLocationSortOrder((prev) => {
                      if (prev === 'none') return 'asc';
                      if (prev === 'asc') return 'desc';
                      return 'none';
                    });
                  }}
                  className="flex items-center gap-1 hover:text-indigo-600 focus:outline-none"
                >
                  Location
                  {locationSortOrder === 'asc' && (
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                  {locationSortOrder === 'desc' && (
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </th>
              {/* Pursue column removed per request */}
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Score</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Initial Email</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Conversation</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Latest Reply</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Contact Status</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white/40">
            {tableLoading ? (
              <tr>
                <td colSpan={12} className="px-4 py-6 text-center text-xs text-gray-500 sm:text-sm">Loading leads...</td>
              </tr>
            ) : filteredLeads.length > 0 ? (
              visibleFilteredLeads.map((lead, index) => {
                const rowId = lead.id ?? `${lead.LeadName ?? 'lead'}-${lead.Email ?? index}`;
                const companyReportText = lead.CompanyReport ?? '';
                const jobListingsText = lead.JobLisitings ?? lead.JobListings ?? '';
                const painPointText = lead.PainPointReport ?? '';
                const companyUrl = lead.URL ?? lead.Url ?? lead.url ?? '';
                const companiesHouseHit = Number(lead.CompaniesHouseHit ?? lead.companieshousehit ?? 0) === 1;
                const filterValue = Number(lead.Filter ?? 0);
                const callScriptText = String(lead.CallScript ?? lead.callScript ?? '').trim();
                const hasCallScript = callScriptText.length > 0;
                const isContactedLead = Boolean(parseLeadContactDate(lead));
                const isSingleEnrichRunning = singleLeadEnrichingId === lead.id;
                const latestReplyEmail = String(lead?.LatestReplyEmail ?? lead?.Email ?? '').trim();
                const latestReplyDraft = String(latestReplyDrafts[lead.id] ?? lead?.LatestReply ?? '');
                const conversationText = String(lead?.Conversation ?? '').trim();
                const latestReplyIsPending = isLatestReplyPending(lead);
                const isExpanded = expandedRows.has(rowId);
                // Use Companies House hit as the visual accent instead of Pursue
                const summaryAccent = companiesHouseHit ? 'bg-emerald-100/45' : 'bg-sky-100/40';
                const detailAccent = companiesHouseHit ? 'bg-emerald-100/35' : 'bg-sky-100/30';
                const summaryBorderClass = isContactedLead ? 'border-amber-400' : companiesHouseHit ? 'border-emerald-400' : 'border-red-400';
                const detailBorderClass = companiesHouseHit ? 'border-emerald-200' : 'border-red-200';

                const renderLongFormCell = (text, viewLabel) => {
                  if (!text) {
                    return <span className="text-gray-400">No data</span>;
                  }

                  const normalizedText = typeof text === 'string' ? text : JSON.stringify(text, null, 2);

                  return (
                    <div className="flex flex-col gap-1">
                      <div className="max-h-16 overflow-hidden text-xs text-slate-600 sm:text-sm">
                        {normalizedText.length > 160 ? `${normalizedText.slice(0, 160)}...` : normalizedText}
                      </div>
                      <button
                        type="button"
                        onClick={() => openModal(viewLabel, normalizedText)}
                        className="self-start text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        View {viewLabel}
                      </button>
                    </div>
                  );
                };

                const renderDetail = (label, value) => (
                  <div className="flex flex-col">
                    <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">{label}</span>
                    <span className="text-xs text-slate-700 sm:text-sm">{value ?? '-'}</span>
                  </div>
                );

                return (
                  <React.Fragment key={rowId}>
                    <tr className={`text-slate-700 transition-colors duration-150 ${summaryAccent} hover:bg-white/65`}>
                      <td className={`px-2 py-2 align-middle border-l-4 ${summaryBorderClass}`}>
                        <div className="flex items-center gap-1">
                          {isContactedLead ? (
                            <span className="text-base text-amber-500" title="Contacted">
                              ★
                            </span>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => toggleRowExpansion(rowId)}
                            className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                          >
                            {isExpanded ? '▾' : '▸'}
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs font-semibold text-slate-900 sm:text-sm">
                        <span className="inline-flex items-center gap-1.5">
                          <UserCircleIcon className="h-4 w-4 text-slate-500" />
                          {lead.LeadName ?? '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs sm:text-sm">
                        <span className="inline-flex items-center gap-1.5">
                          <BuildingOffice2Icon className="h-4 w-4 text-slate-500" />
                          {lead.Company ?? '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs sm:text-sm">{lead.Sector ?? '-'}</td>
                      <td className="px-3 py-2 text-xs sm:text-sm">{lead.Location ?? '-'}</td>
                      <td className="px-3 py-2 text-xs sm:text-sm text-center whitespace-nowrap">
                        <div className="inline-flex items-center justify-center">
                          <div className="h-6 w-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold">
                            {lead?.Credits != null && String(lead?.Credits).trim() !== '' ? String(lead.Credits) : '-'}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 sm:text-sm">
                        {initialEmailEditingId === lead.id ? (
                          <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
                            Editing in progress above...
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="text-xs text-slate-700 font-semibold sm:text-sm">{lead?.Subject ?? ''}</div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 shadow-inner">
                              {lead.InitialEmail ? (
                                <p className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-left text-slate-700 text-xs sm:text-sm">
                                  {lead.InitialEmail}
                                </p>
                              ) : (
                                <p className="text-xs text-slate-400 sm:text-sm">No email drafted.</p>
                              )}
                            </div>
                            {initialEmailMessages[lead.id]?.success && (
                              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-600">
                                {initialEmailMessages[lead.id].success}
                              </div>
                            )}
                            {initialEmailMessages[lead.id]?.error && (
                              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                                {initialEmailMessages[lead.id].error}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => openInitialEmailEditor(lead)}
                              className="inline-flex w-full items-center justify-center rounded-md border border-indigo-200 px-3 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed sm:w-auto"
                              disabled={deletingLeadId !== null || !lead.id}
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 sm:text-sm">
                        {conversationText ? (
                          <div className="space-y-1">
                            <div className="max-h-24 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-slate-50 p-2">
                              {conversationText}
                            </div>
                            <button
                              type="button"
                              onClick={() => openModal('Conversation', conversationText)}
                              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                            >
                              View full conversation
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-400">No conversation</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 sm:text-sm">
                        <div className="space-y-2">
                          <div className="text-[11px] text-slate-600">
                            <span className="font-semibold text-slate-800">To:</span> {latestReplyEmail || '-'}
                          </div>
                          <textarea
                            rows={3}
                            value={latestReplyDraft}
                            onChange={(event) => handleLatestReplyDraftChange(lead.id, event.target.value)}
                            placeholder="Edit latest reply before sending"
                            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <div className="flex items-center justify-between gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${latestReplyIsPending ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                              {latestReplyIsPending ? 'Has Reply' : 'No Reply'}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openLatestReplyEditor(lead)}
                                className="inline-flex items-center gap-1 rounded-md border border-indigo-300 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50"
                                disabled={!lead.id}
                              >
                                Full Screen
                              </button>
                              <button
                                type="button"
                                onClick={() => handleApproveLatestReply(lead)}
                                className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
                                disabled={!lead.id || latestReplySendingId === lead.id}
                              >
                                <PaperAirplaneIcon className="h-3.5 w-3.5" />
                                {latestReplySendingId === lead.id ? 'Sending...' : 'Send'}
                              </button>
                            </div>
                          </div>
                          {latestReplyMessages[lead.id]?.success && (
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700">
                              {latestReplyMessages[lead.id].success}
                            </div>
                          )}
                          {latestReplyMessages[lead.id]?.error && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-600">
                              {latestReplyMessages[lead.id].error}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 sm:text-sm">
                        <div className="space-y-1">
                          <div>
                            <span className="font-semibold text-slate-800">Progress:</span>{' '}
                            <select
                              value={['No Reply', 'Interested', 'Sale'].includes(String(lead?.Progress || '').trim()) ? String(lead.Progress).trim() : 'No Reply'}
                              onChange={(event) => handleProgressChange(lead.id, event.target.value)}
                              disabled={!lead.id || progressSavingLeadId === lead.id}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="No Reply">No Reply</option>
                              <option value="Interested">Interested</option>
                              <option value="Sale">Sale</option>
                            </select>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-800">Sent To:</span>{' '}
                            <span className="break-words">{lead?.SentTo || '-'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-800">Contacted:</span>{' '}
                            <span>{lead?.LastContacted ? new Date(lead.LastContacted).toLocaleDateString() : '-'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 sm:text-sm">
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                          {filterValue !== 1 && lead.id ? (
                            <button
                              type="button"
                              onClick={() => handleSingleLeadEnrich(lead)}
                              className="rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-purple-500/30 hover:from-purple-500 hover:to-blue-500"
                              disabled={deletingLeadId !== null || singleLeadEnrichingId !== null}
                            >
                              {isSingleEnrichRunning ? 'Enriching...' : 'Enrich Lead'}
                            </button>
                          ) : null}
                          {!hasCallScript && (
                            <button
                              type="button"
                              onClick={() => handleGenerateCallScript(lead)}
                              className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-emerald-600 to-sky-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-emerald-500/30 hover:from-emerald-500 hover:to-sky-500"
                              disabled={deletingLeadId !== null || singleLeadEnrichingId !== null || callScriptGeneratingId !== null || !canUseAiAndEnrich}
                            >
                              {callScriptGeneratingId === lead.id ? 'Generating Script...' : 'Generate Call Script'}
                            </button>
                          )}
                          {!lead.id ? null : (
                            <button
                              type="button"
                              onClick={() => handleDeleteLead(lead.id)}
                              className="w-full rounded-md bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 sm:w-auto"
                              disabled={deletingLeadId !== null || singleLeadEnrichingId !== null}
                            >
                              {deletingLeadId === lead.id ? 'Deleting...' : 'Delete'}
                            </button>
                          )}
                          {hasInsightAccount && isContactedLead && lead.id ? (
                            <button
                              type="button"
                              onClick={() => handleAddToCrm(lead)}
                              className="w-full rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400 sm:w-auto"
                              disabled={crmAddingLeadId === lead.id}
                            >
                              {crmAddingLeadId === lead.id ? 'Continuing...' : 'Continue'}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className={`text-slate-700 ${detailAccent}`}>
                        <td colSpan={12} className={`border-l-4 ${detailBorderClass} bg-white px-4 sm:px-6 py-4 shadow-sm`}>
                          <div className="space-y-6 text-slate-700">
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                              {renderDetail('Occupation', lead.Occupation || '-')}
                              {renderDetail('Company Size', lead.CompanySize || '-')}
                              {renderDetail('Company Number', lead.CompanyNumber || '-')}
                              {renderDetail('Companies House Hit', companiesHouseHit ? 'Yes' : 'No')}
                              {renderDetail('Website', lead.Website ? (
                                <a href={lead.Website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{lead.Website}</a>
                              ) : '-')}
                              {renderDetail('Company URL', companyUrl ? (
                                <a href={companyUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{companyUrl}</a>
                              ) : '-')}
                              {renderDetail('Email', lead.Email || '-')}
                              
                              {renderDetail('Telephone', lead.Telephone || '-')}
                              {renderDetail('Address', lead.Address || '-')}
                              {renderDetail('Town', lead.Town || '-')}
                              {renderDetail('Contacted', lead.Contacted ? new Date(lead.Contacted).toLocaleString() : 'Not contacted')}
                              {renderDetail('Progress', lead.Progress || '-')}
                              {renderDetail('Sent To', lead.SentTo || '-')}
                              {renderDetail('Last Contacted', lead.LastContacted ? new Date(lead.LastContacted).toLocaleDateString() : '-')}
                              {renderDetail('Latest Reply Sent', lead.LatestReplySent ?? '-')}
                              {renderDetail('Latest Reply Email', lead.LatestReplyEmail || '-')}
                              {renderDetail('Initial Email', lead.InitialEmail ?? '-')}
                              {renderDetail('Latest Reply', lead.LatestReply ?? '-')}
                              {renderDetail('Conversation', lead.Conversation ?? '-')}
                              {renderDetail('Responded', lead.Responded ?? '-')}
                              {/* Email Template removed from expanded table view per request */}
                              {renderDetail('Created At', lead.CreatedAt ? new Date(lead.CreatedAt).toLocaleString() : '-')}
                            </div>

                            <div className="grid gap-4 lg:grid-cols-3">
                              <div className="flex flex-col gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Company Report</span>
                                {renderLongFormCell(companyReportText, 'Company Report')}
                              </div>
                              <div className="flex flex-col gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Job Listings</span>
                                {renderLongFormCell(jobListingsText, 'Job Listings')}
                              </div>
                              <div className="flex flex-col gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pain Points</span>
                                {renderLongFormCell(painPointText, 'Pain Points')}
                              </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Call Script</span>
                                {hasCallScript ? (
                                  <button
                                    type="button"
                                    onClick={() => openModal('Call Script', callScriptText)}
                                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                                  >
                                    View Full Script
                                  </button>
                                ) : null}
                              </div>
                              {hasCallScript ? (
                                <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 sm:text-sm">
                                  {callScriptText}
                                </div>
                              ) : (
                                <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-400 sm:text-sm">
                                  No call script generated.
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={12} className="px-4 py-6 text-center text-xs text-gray-500 sm:text-sm">No leads found.</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
        {!tableLoading && filteredLeads.length > 0 && hasMoreFilteredLeads && (
          <div ref={leadsLoadMoreRef} className="py-4 text-center text-xs text-slate-500">
            Loading more leads... ({visibleFilteredLeads.length}/{filteredLeads.length})
          </div>
        )}
      </div>

      </div>
      {smtpModalOpen && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-slate-900/60 backdrop-blur-sm">
          <div className="mx-auto flex min-h-screen w-full max-w-3xl items-center justify-center px-3 py-6 sm:px-4 sm:py-10">
            <div className="w-full rounded-2xl border border-white/55 bg-white shadow-[0_25px_70px_rgba(14,116,144,0.35)] backdrop-blur-xl">
              <div className="flex flex-col gap-3 border-b border-white/50 bg-white/40 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">SMTP Data</h2>
                  <p className="text-xs text-gray-600 sm:text-sm">Configure sender settings for outbound emails.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSmtpModalOpen(false)}
                  className="w-full rounded-lg border border-white/60 bg-white/75 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-white sm:w-auto"
                >
                  Close
                </button>
              </div>
              <div className="grid gap-4 px-4 py-5 sm:grid-cols-2 sm:px-6 sm:py-6">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Provider</label>
                  <select
                    value={smtpProvider}
                    onChange={(event) => handleSmtpProviderChange(event.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {Object.entries(smtpProviderPresets).map(([key, provider]) => (
                      <option key={key} value={key}>{provider.label}</option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">SMTP Host</label>
                  <input
                    type="text"
                    value={smtpForm.host}
                    onChange={(event) => handleSmtpFieldChange('host', event.target.value)}
                    placeholder="smtp.example.com"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Port</label>
                  <input
                    type="number"
                    value={smtpForm.port}
                    onChange={(event) => handleSmtpFieldChange('port', event.target.value)}
                    placeholder="587"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Secure (SSL/TLS)</label>
                  <select
                    value={smtpForm.secure ? 'true' : 'false'}
                    onChange={(event) => handleSmtpFieldChange('secure', event.target.value === 'true')}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">SMTP Username</label>
                  <input
                    type="text"
                    value={smtpForm.username}
                    onChange={(event) => handleSmtpFieldChange('username', event.target.value)}
                    placeholder="username"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">SMTP Password</label>
                  <input
                    type="password"
                    value={smtpForm.password}
                    onChange={(event) => handleSmtpFieldChange('password', event.target.value)}
                    placeholder="password"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">From Email</label>
                  <input
                    type="email"
                    value={smtpForm.fromEmail}
                    onChange={(event) => handleSmtpFieldChange('fromEmail', event.target.value)}
                    placeholder="sales@yourdomain.com"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="sm:col-span-2 rounded-lg border border-sky-200 bg-white px-4 py-3 text-xs text-slate-700">
                  <p className="font-semibold text-slate-900">SMTP Port Guide</p>
                  <ul className="mt-2 list-disc pl-4 space-y-1">
                    <li>Gmail: smtp.gmail.com → 587 (STARTTLS) or 465 (SSL)</li>
                    <li>Outlook / Microsoft 365: smtp.office365.com → 587 (STARTTLS)</li>
                    <li>iCloud: smtp.mail.me.com → 587 (STARTTLS)</li>
                    <li>Yahoo: smtp.mail.yahoo.com → 465 (SSL)</li>
                    <li>IONOS: smtp.ionos.co.uk → 465 (SSL)</li>
                  </ul>
                  <p className="mt-2 text-[11px] text-slate-500">Most providers require an app password when MFA is enabled.</p>
                </div>

                <div
                  className={classNames(
                    'sm:col-span-2 rounded-md border px-3 py-2 text-sm',
                    smtpConnected === true
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : smtpConnected === false
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-slate-200 bg-white text-slate-600'
                  )}
                >
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <span>
                      {smtpChecking ? 'Checking connection...' : smtpConnectionMessage || 'Connection status not checked yet.'}
                    </span>
                    {smtpLastCheckedAt ? <span className="text-[11px] opacity-75">Last checked: {smtpLastCheckedAt}</span> : null}
                  </div>
                </div>
              </div>

              {smtpError && (
                <div className="mx-4 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 sm:mx-6">
                  {smtpError}
                </div>
              )}
              {smtpFeedback && (
                <div className="mx-4 mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 sm:mx-6">
                  {smtpFeedback}
                </div>
              )}

              <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6">
                <button
                  type="button"
                  onClick={() => checkSmtpConnection()}
                  disabled={smtpChecking}
                  className="w-full rounded-md border border-sky-300 bg-white px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-60 sm:w-auto"
                >
                  {smtpChecking ? 'Checking...' : 'Check Connection'}
                </button>
                <button
                  type="button"
                  onClick={() => setSmtpModalOpen(false)}
                  className="w-full rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveSmtpSettings}
                  disabled={smtpSaving}
                  className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 sm:w-auto"
                >
                  {smtpSaving ? 'Saving...' : 'Save SMTP Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {latestReplyEditingLead && (
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-slate-900/70 backdrop-blur-sm">
          <div className="mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-3 py-4 sm:px-6 sm:py-8">
            <div className="w-full rounded-2xl border border-white/55 bg-white shadow-[0_25px_70px_rgba(30,64,175,0.35)]">
              <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 sm:text-xl">Edit Latest Reply</h2>
                  <p className="text-xs text-slate-600 sm:text-sm">
                    {latestReplyEditingLead?.LeadName ?? 'Lead'}{latestReplyEditingLead?.Company ? ` - ${latestReplyEditingLead.Company}` : ''}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">To: {String(latestReplyEditingLead?.LatestReplyEmail ?? latestReplyEditingLead?.Email ?? '-').trim() || '-'}</p>
                </div>
                <button
                  type="button"
                  onClick={closeLatestReplyEditor}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
                >
                  Close
                </button>
              </div>

              <div className="px-4 py-4 sm:px-6">
                <textarea
                  value={String(latestReplyDrafts[latestReplyEditingLead.id] ?? latestReplyEditingLead?.LatestReply ?? '')}
                  onChange={(event) => handleLatestReplyDraftChange(latestReplyEditingLead.id, event.target.value)}
                  placeholder="Edit latest reply before sending"
                  className="h-[65vh] w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />

                {latestReplyMessages[latestReplyEditingLead.id]?.success && (
                  <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {latestReplyMessages[latestReplyEditingLead.id].success}
                  </div>
                )}
                {latestReplyMessages[latestReplyEditingLead.id]?.error && (
                  <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {latestReplyMessages[latestReplyEditingLead.id].error}
                  </div>
                )}
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6">
                <button
                  type="button"
                  onClick={closeLatestReplyEditor}
                  className="w-full rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await handleApproveLatestReply(latestReplyEditingLead);
                    closeLatestReplyEditor();
                  }}
                  className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400 sm:w-auto"
                  disabled={latestReplySendingId === latestReplyEditingLead.id}
                >
                  {latestReplySendingId === latestReplyEditingLead.id ? 'Sending...' : 'Send Latest Reply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Granular Bulk Send Modal */}
      {granularSendModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
          <div className="w-full max-w-4xl overflow-hidden rounded-2xl border border-white/55 bg-white/75 shadow-[0_20px_60px_rgba(16,185,129,0.35)] backdrop-blur-xl">
            <div className="border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-4 sm:px-6">
              <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Send Emails by Sector & Location</h2>
              <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                Specify how many emails to send for each sector/location combination.
              </p>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-4 py-4 sm:px-6">
              {Object.keys(sectorLocationBreakdown).length === 0 ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  No drafted initial emails found.
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.keys(sectorLocationBreakdown)
                    .sort((a, b) => {
                      const aData = sectorLocationBreakdown[a];
                      const bData = sectorLocationBreakdown[b];
                      const sectorCompare = (aData.sector ?? '').localeCompare(bData.sector ?? '');
                      if (sectorCompare !== 0) return sectorCompare;
                      return (aData.location ?? '').localeCompare(bData.location ?? '');
                    })
                    .map((key) => {
                      const { sector, location, count } = sectorLocationBreakdown[key];
                      const qty = granularSendQuantities[key] ?? count;
                      return (
                        <div key={key} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800">
                                  {sector}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
                                  {location}
                                </span>
                              </div>
                              <p className="mt-1 text-sm text-gray-600">{count} drafted email(s) available</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium text-gray-700">Send:</label>
                              <input
                                type="number"
                                min="0"
                                max={count}
                                value={qty}
                                onChange={(e) => handleGranularSendQuantityChange(key, e.target.value)}
                                className="w-20 rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                              />
                              <span className="text-sm text-gray-500">/ {count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {granularSendFeedback && (
                <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {granularSendFeedback}
                </div>
              )}
              {granularSendError && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {granularSendError}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeGranularSendModal}
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 sm:w-auto"
                  disabled={granularSendRunning}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGranularSendEmails}
                  className="w-full rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
                  disabled={granularSendRunning || Object.keys(sectorLocationBreakdown).length === 0}
                >
                  {granularSendRunning ? 'Sending...' : 'Send Emails'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 px-3 backdrop-blur-sm sm:px-4">
          <div className="relative w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl border border-white/55 bg-white/75 shadow-[0_20px_60px_rgba(37,99,235,0.35)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-white/50 bg-white/40 px-4 py-4 sm:px-6">
              <h2 className="text-base font-semibold text-gray-900 sm:text-lg">{modalState.title}</h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="sr-only">Close modal</span>
                ×
              </button>
            </div>
            <div className="overflow-y-auto px-4 py-4 text-sm text-gray-700 whitespace-pre-wrap sm:px-6">
              {modalState.body}
            </div>
            <div className="flex justify-end border-t border-gray-200 px-4 py-3 sm:px-6">
              <button
                type="button"
                onClick={closeModal}
                className="w-full rounded-md bg-indigo-600 px-4 py-2 font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      
      </div>
    </>
  );
}
